// src/components/Home.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  Divider,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  SvgIcon,
  Chip,
  Stack
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot, orderBy, addDoc, serverTimestamp, limit, getDocs, getCountFromServer, Timestamp, startAfter, doc, getDoc, setDoc } from "firebase/firestore";
import { db, auth, googleProvider } from "../firebase";
import { signInWithPopup } from "firebase/auth";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoginIcon from '@mui/icons-material/Login';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import StarIcon from '@mui/icons-material/Star';
import { formatDistanceToNow } from 'date-fns';
import { ko } from 'date-fns/locale';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isInAppBrowser } from "../utils/browserDetect";
import { handleGoogleLogin } from "../services/authService";
import InAppBrowserModal from "./InAppBrowserModal";
import { debounce } from 'lodash';
import SEO from './SEO';

// 텍스트 자르기 헬퍼 함수 추가
const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Helper function to format date relative to now
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  let date;
  if (timestamp instanceof Timestamp) {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    return ''; // Or handle other types if necessary
  }
  return formatDistanceToNow(date, { addSuffix: true, locale: ko });
};

// --- NEW Action Buttons Component ---
const ActionButtons = ({ currentUser, navigate }) => {
  // 중복 없는 랜덤 배경 이미지 선택 함수
  const getUniqueRandomBackgroundImages = (count) => {
    const availableImages = Array.from({ length: 8 }, (_, i) => i + 1); // 1-8
    const selectedImages = [];
    
    for (let i = 0; i < count; i++) {
      const randomIndex = Math.floor(Math.random() * availableImages.length);
      const selectedNumber = availableImages.splice(randomIndex, 1)[0];
      selectedImages.push(`/images/home-0${selectedNumber}.png`);
    }
    
    return selectedImages;
  };

  // 3개의 고유한 배경 이미지 선택
  const backgroundImages = getUniqueRandomBackgroundImages(3);

  const actions = [
    {
      icon: <AddCircleOutlineIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />,
      label: '책 추가',
      onClick: () => navigate('/search', { state: { focusSearch: true } }),
      color: '#FFFFFF',
      bgColor: 'transparent',
      backgroundImage: backgroundImages[0],
      isBookAdd: true,
      isPrimary: true
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />,
      label: '통계',
      onClick: () => {
        if (!currentUser) {
          // 비로그인 상태에서는 mypage로 이동
          navigate('/mypage');
        } else {
          // 로그인 상태에서는 Ranking 뷰가 활성화된 상태로 이동
          navigate('/mypage', { state: { showRanking: true } });
        }
      },
      color: '#FFFFFF',
      bgColor: 'transparent',
      backgroundImage: backgroundImages[1],
      isBookAdd: true
    },
    {
      icon: <SportsEsportsIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />,
      label: '책 맞추기',
      onClick: () => {
        if (!currentUser) {
          // 비로그인 상태에서는 mypage로 이동
          navigate('/mypage');
        } else {
          // 로그인 상태에서는 Game 뷰가 활성화된 상태로 이동
          navigate('/mypage', { state: { showGame: true } });
        }
      },
      color: '#FFFFFF',
      bgColor: 'transparent',
      backgroundImage: backgroundImages[2],
      isBookAdd: true
    }
  ];

  return (
    <Box sx={{ 
      px: { xs: 1.5, sm: 2 }, 
      mb: { xs: 2, sm: 3 },
      position: 'relative',
      zIndex: 100,
      pointerEvents: 'auto'
    }}>
      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 1, sm: 1.5, md: 2 },
        width: '100%'
      }}>
        {actions.map((action, index) => (
          <Button
            key={index}
            variant="contained"
            onClick={action.onClick}
            sx={{
              flex: 1,
              aspectRatio: '1 / 1',
              bgcolor: action.isBookAdd ? 'transparent' : action.bgColor,
              color: action.color,
              borderRadius: { xs: '12px', sm: '16px' },
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              position: 'relative',
              zIndex: 101,
              display: 'block',
              overflow: 'hidden',
              backgroundImage: action.isBookAdd ? `url(${action.backgroundImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              '&::before': action.isBookAdd ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                zIndex: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              } : {},
              '&:hover': {
                bgcolor: action.isBookAdd ? 'transparent' : action.bgColor,
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                '&::before': action.isBookAdd ? {
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                } : {},
                '& .icon-container': {
                  transform: 'scale(1.1)',
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                }
              },
              '&:active': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              '&:focus': {
                outline: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 0 0 2px rgba(255,255,255,0.3)',
                '& .icon-container': {
                  transform: 'scale(1.05)',
                }
              }
            }}
          >
            <Box sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              pointerEvents: 'none'
            }}>
              {/* 좌측 상단 텍스트 */}
              <Typography sx={{ 
                position: 'absolute',
                top: { xs: 12, sm: 16, md: 20, lg: 24, xl: 28 },
                left: { xs: 12, sm: 16, md: 20, lg: 24, xl: 28 },
                fontSize: action.isPrimary 
                  ? { xs: '24px', sm: '28px', md: '32px', lg: '36px', xl: '40px' }
                  : { xs: '22px', sm: '26px', md: '30px', lg: '34px', xl: '38px' }, 
                fontWeight: action.isPrimary ? 900 : 800, 
                lineHeight: 1.1,
                color: action.isBookAdd ? '#FFFFFF' : action.color,
                textShadow: action.isBookAdd ? '0 2px 6px rgba(0,0,0,0.7)' : 'none',
                fontFamily: 'NanumSquareEB',
                textAlign: 'left',
                wordBreak: 'keep-all',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                letterSpacing: action.isPrimary ? '-0.03em' : '-0.02em',
                maxWidth: '65%',
                pointerEvents: 'none'
              }}>
                {action.label}
              </Typography>
              
              {/* 우측 하단 그래픽 요소 */}
              <Box className="icon-container" sx={{ 
                position: 'absolute',
                bottom: { xs: 12, sm: 16, md: 20, lg: 24, xl: 28 },
                right: { xs: 12, sm: 16, md: 20, lg: 24, xl: 28 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: action.isPrimary 
                  ? { xs: 42, sm: 50, md: 58, lg: 66, xl: 74 }
                  : { xs: 46, sm: 54, md: 62, lg: 70, xl: 78 },
                height: action.isPrimary 
                  ? { xs: 42, sm: 50, md: 58, lg: 66, xl: 74 }
                  : { xs: 46, sm: 54, md: 62, lg: 70, xl: 78 },
                borderRadius: { xs: '12px', sm: '14px', md: '16px', lg: '18px' },
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(4px)',
                transition: 'all 0.3s ease',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
                pointerEvents: 'auto'
              }}>
                <Box sx={{
                                  fontSize: action.isPrimary 
                  ? { xs: 22, sm: 26, md: 30, lg: 34, xl: 38 }
                  : { xs: 24, sm: 28, md: 32, lg: 36, xl: 40 },
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {React.cloneElement(action.icon, { 
                    sx: { fontSize: 'inherit', color: 'inherit' }
                  })}
                </Box>
              </Box>
            </Box>
          </Button>
        ))}
      </Box>
    </Box>
  );
};

// --- NEW Enhanced Book Card Component ---
const EnhancedBookCard = ({ bookData, currentUser, navigate }) => {
  const [showAllHighlights, setShowAllHighlights] = useState(false);
  const maxHighlightsToShow = 3;

  const handleNavigation = () => {
    const bookDocId = bookData.bookDocId;
    const userUid = bookData.uid;
    if (bookDocId && userUid) {
      console.log('Navigating to book detail:', { bookDocId, userUid });
      navigate(`/book/${bookDocId}?uid=${userUid}`);
    } else {
      console.warn('Missing bookDocId or userUid for navigation:', { bookDocId, userUid });
    }
  };

  // 하이라이트를 랜덤하게 섞고 최대 3개만 표시 (컴포넌트 마운트 시 한 번만 실행)
  const displayedHighlights = useMemo(() => {
    const highlights = Array.isArray(bookData.highlights) ? bookData.highlights : [];
    
    // 디버깅을 위한 로그
    if (highlights.length > 0) {
      console.log(`EnhancedBookCard: ${bookData.bookTitle} has ${highlights.length} highlights`);
    }
    
    if (showAllHighlights) {
      return highlights;
    }
    
    // 랜덤하게 섞고 최대 3개만 선택
    const shuffled = [...highlights].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, maxHighlightsToShow);
  }, [bookData.highlights, showAllHighlights, maxHighlightsToShow, bookData.bookTitle, bookData.id]); // bookData.id 추가로 더 안정적인 랜덤화

  // 하이라이트 텍스트 정제 함수
  const sanitizeHighlightText = (text) => {
    if (!text || typeof text !== 'string') return '하이라이트 내용이 없습니다';
    
    // HTML 태그 제거
    const cleanText = text.replace(/<[^>]*>/g, '');
    
    // 연속된 공백 정리
    const normalizedText = cleanText.replace(/\s+/g, ' ').trim();
    
    return normalizedText || '하이라이트 내용이 없습니다';
  };

  return (
    <Paper
      onClick={handleNavigation}
      elevation={0}
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.04)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
        '&:active': {
          transform: 'translateY(-2px)',
        },
        mb: 3
      }}
    >
      {/* A) Header: 제목(좌측) + 썸네일(우측) */}
      <Box sx={{ px: 2, pt: 2, pb: bookData.memo && bookData.memo.trim() !== '' ? 0 : 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, minHeight: 84 }}>
          {/* 제목: 좌측 배치, 썸네일 높이와 시각적 균형 */}
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              minHeight: 84,
              py: 0.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'NanumSquareEB, sans-serif',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: 1.35,
                color: '#333',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {bookData.bookTitle || '제목 없음'}
            </Typography>
          </Box>

          {/* 썸네일: 우측 배치, 56x84px */}
          <Box
            sx={{
              width: 56,
              height: 84,
              minWidth: 56,
              flexShrink: 0,
              borderRadius: '6px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.02)',
              }
            }}
          >
            <img
              src={bookData.bookThumbnail || '/images/no-cover.png'}
              alt={`${bookData.bookTitle || '책 커버'}${bookData.authors ? ` - ${bookData.authors.join(', ')}` : ''}`}
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none'
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* B) Memo: 메인 콘텐츠 - 절대 잘림 없음, 배경 강조 */}
      {bookData.memo && bookData.memo.trim() !== '' && (
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              bgcolor: 'rgba(0,0,0,0.04)',
              borderRadius: '8px',
            }}
          >
            <Typography
              component="div"
              sx={{
                color: '#333',
                fontSize: '0.9375rem',
                fontWeight: 500,
                lineHeight: 1.65,
                whiteSpace: 'pre-wrap',
                overflow: 'visible',
                display: 'block',
                wordBreak: 'break-word',
              }}
            >
              {bookData.memo}
            </Typography>
          </Box>
        </Box>
      )}

      {/* C) Highlights - 보조 콘텐츠, 2~3줄 clamp */}
      {Array.isArray(bookData.highlights) && bookData.highlights.length > 0 ? (
        <>
          <Divider sx={{ mx: 2, borderColor: 'rgba(0,0,0,0.06)' }} />
          <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
            {/* 책 하이라이트 라벨 */}
            <Typography sx={{ fontSize: '0.75rem', color: '#888', fontWeight: 500, mb: 1.5 }}>
              책 하이라이트
            </Typography>
            {/* 태그가 있는 경우 태그 표시 - 최대 3개까지만 */}
            {bookData.tags && bookData.tags.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {bookData.tags.slice(0, 3).map((tag, index) => (
                    <Box
                      key={index}
                      sx={{
                        px: 0,
                        py: 0.5,
                        bgcolor: '#f0f0f0',
                        borderRadius: '12px',
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: '#e8e8e8',
                          borderColor: '#ccc'
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: 'NanumSquareR'
                        }}
                      >
                        #{tag}
                      </Typography>
                    </Box>
                  ))}
                  {bookData.tags.length > 3 && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        bgcolor: '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          fontSize: '11px',
                          fontWeight: 500,
                          fontFamily: 'NanumSquareR'
                        }}
                      >
                        +{bookData.tags.length - 3}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            
            {/* 문장 목록 */}
            <Box sx={{ pl: 0 }}>
              {displayedHighlights.map((highlight, index) => (
                <Box key={highlight.id || index} sx={{ 
                  mb: 2.5,
                  position: 'relative',
                  cursor: 'default'
                }}>
                  {/* 스레드 연결선 - 제거됨 (공간 확보를 위해) */}
                  
                  {/* 문장 내용 */}
                  <Box 
                    className="highlight-card"
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    sx={{
                      position: 'relative',
                      zIndex: 40,
                      bgcolor: 'transparent',
                      borderRadius: '8px',
                      p: 1,
                      border: 'none',
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      '&:hover': {
                        backgroundColor: 'rgba(26, 115, 232, 0.03)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)'
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      {/* 인용 부호 */}
                      <Typography
                        className="quote-mark"
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          left: -4,
                          fontSize: '24px',
                          color: '#333',
                          fontFamily: 'serif',
                          opacity: 0.6,
                          zIndex: 41,
                          lineHeight: 1,
                          userSelect: 'none',
                          pointerEvents: 'auto',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            color: '#1a73e8',
                            opacity: 0.8,
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        "
                      </Typography>
                      
                      {/* 문장 텍스트 */}
                      <Typography
                        className="highlight-text"
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          // 하이라이트로 이동하는 로직
                          const bookDocId = bookData.bookDocId;
                          const userUid = bookData.uid;
                          const highlightId = highlight.id;
                          
                          if (bookDocId && userUid && highlightId) {
                            console.log('하이라이트 클릭:', { bookDocId, userUid, highlightId });
                            // BookDetail 페이지로 이동하면서 하이라이트 ID를 쿼리 파라미터로 전달
                            navigate(`/book/${bookDocId}?uid=${userUid}&highlight=${highlightId}`);
                          } else {
                            console.warn('하이라이트 클릭 - 필요한 데이터가 없음:', { bookDocId, userUid, highlightId });
                          }
                        }}
                        sx={{
                          color: '#2c3e50',
                          fontSize: '14px',
                          lineHeight: 1.7,
                          fontWeight: 400,
                          fontFamily: 'NanumSquareR',
                          userSelect: 'text',
                          wordBreak: 'break-word',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          position: 'relative',
                          zIndex: 40,
                          pl: 0.5,
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            color: '#1a73e8',
                            backgroundColor: 'rgba(26, 115, 232, 0.05)',
                            borderRadius: '8px',
                            padding: '8px',
                            margin: '-8px',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(26, 115, 232, 0.15)'
                          }
                        }}
                      >
                        {sanitizeHighlightText(highlight.text)}
                      </Typography>
                    </Box>
                    
                    {/* 문장 메타 정보 - 숨김 처리 */}
                    {/* 날짜 정보는 제거하여 UI를 단순화 */}
                  </Box>
                </Box>
              ))}
            </Box>
            
            {/* Show More Button */}
            {Array.isArray(bookData.highlights) && bookData.highlights.length > maxHighlightsToShow && displayedHighlights.length > 0 && (
              <Box sx={{ pl: 0, mt: 2 }}>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllHighlights(!showAllHighlights);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                  sx={{
                    color: '#333',
                    fontSize: '13px',
                    fontWeight: 600,
                    textTransform: 'none',
                    pl: 1,
                    minWidth: 'auto',
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    bgcolor: 'transparent',
                    fontFamily: 'NanumSquareB',
                    position: 'relative',
                    zIndex: 50,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(26, 115, 232, 0.05)',
                      color: '#1a73e8',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      margin: '-4px -8px'
                    },
                    
                  }}
                >
                  {showAllHighlights ? '접기' : `${currentUser && currentUser.uid === bookData.uid ? '내' : (bookData.userName + '님의')} 기록 더보기 (${Array.isArray(bookData.highlights) ? bookData.highlights.length - maxHighlightsToShow : 0}개)`}
                </Button>
              </Box>
            )}
          </Box>
        </>
      ) : (
        // 하이라이트가 없을 때의 UI (태그 표시)
        <>
          <Divider sx={{ mx: 3, borderColor: '#f0f0f0' }} />
          <Box sx={{ p: 3, pt: 2 }}>
            {/* 문장 헤더 */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                width: 3, 
                height: 3, 
                borderRadius: '50%', 
                bgcolor: '#ddd',
                flexShrink: 0
              }} />
              <Typography
                variant="caption"
                sx={{
                  color: '#666',
                  fontSize: '12px',
                  fontWeight: 500,
                  fontFamily: 'NanumSquareR'
                }}
              >
                이 책에서 기록한 문장
              </Typography>
            </Box>
            
            <Box sx={{ pl: 0 }}>
              {/* 태그가 있는 경우 태그 표시 - 최대 3개까지만 */}
              {bookData.tags && bookData.tags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {bookData.tags.slice(0, 3).map((tag, index) => (
                      <Box
                        key={index}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          bgcolor: '#f0f0f0',
                          borderRadius: '12px',
                          border: '1px solid #e0e0e0',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: '#e8e8e8',
                            borderColor: '#ccc'
                          }
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#666',
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: 'NanumSquareR'
                          }}
                        >
                          #{tag}
                        </Typography>
                      </Box>
                    ))}
                    {bookData.tags.length > 3 && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          bgcolor: '#f8f9fa',
                          borderRadius: '12px',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#999',
                            fontSize: '11px',
                            fontWeight: 500,
                            fontFamily: 'NanumSquareR'
                          }}
                        >
                          +{bookData.tags.length - 3}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              
              <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                bgcolor: '#fafafa',
                borderRadius: '16px',
                border: '2px dashed #e0e0e0',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: '#333',
                  bgcolor: '#f8f9fa'
                }
              }}>
                {/* 스레드 연결선 - 제거됨 (공간 확보를 위해) */}
                
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%', 
                    bgcolor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}>
                    <Typography sx={{ 
                      fontSize: '12px', 
                      color: '#999',
                      fontWeight: 'bold'
                    }}>
                      📖
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#999',
                      fontSize: '14px',
                      fontFamily: 'NanumSquareR',
                      mb: 1,
                      fontWeight: 500
                    }}
                  >
                    {currentUser && currentUser.uid === bookData.uid ? '아직 문장이 없습니다' : '문장을 확인해보세요'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#bbb',
                      fontSize: '12px',
                      fontFamily: 'NanumSquareR'
                    }}
                  >
                    {currentUser && currentUser.uid === bookData.uid 
                      ? '책을 읽으면서 마음에 드는 문장을 기록해보세요' 
                      : '이 책의 문장을 보려면 카드를 클릭하세요'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* 책 카드 전체 클릭 영역 - 완전히 제거하고 직접 클릭 방식으로 변경 */}
    </Paper>
  );
};

// --- NEW Enhanced Book Cards Container ---
const EnhancedBookCards = ({ currentUser, navigate, followingIds, authLoading }) => {
  const [bookCards, setBookCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedBookIds, setLoadedBookIds] = useState(new Set()); // 중복 방지를 위한 Set
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      loadBookCards(true);
    }
  }, [authLoading]);

  // 스크롤 이벤트 리스너 추가
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // 스크롤이 하단에서 200px 이내에 도달하면 추가 로드
      if (scrollTop + windowHeight >= documentHeight - 200) {
        loadBookCards(false);
      }
    };

    // 스크롤 이벤트 리스너 등록
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loadingMore, hasMore]);

  const loadBookCards = useCallback(async (isInitial = false) => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    
    try {
      const booksRef = collection(db, 'books');
      let bookDocs = [];
      
      // 모든 유저가 등록한 책에 대한 기록을 랜덤하게 표시
      const publicBooksQuery = query(booksRef, orderBy("createdAt", "desc"), limit(100));
      const snapshot = await getDocs(publicBooksQuery);
      const allBooksWithMemo = snapshot.docs.filter(doc => {
        const data = doc.data();
        return data.memo && data.memo.trim() !== "";
      });
      
      if (allBooksWithMemo.length === 0) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }
      
      // 중복 방지: 이미 로드된 책 ID 제외
      const newBooksWithMemo = allBooksWithMemo.filter(doc => !loadedBookIds.has(doc.id));
      
      if (newBooksWithMemo.length === 0) {
        if (snapshot.docs.length < 100) {
          setHasMore(false);
        }
        setLoadingMore(false);
        return;
      }
      
      // 랜덤 정렬하여 다양한 유저의 기록을 보여줌
      const shuffled = newBooksWithMemo.sort(() => 0.5 - Math.random());
      bookDocs = shuffled.slice(0, 15);
      
      if (bookDocs.length < 15) {
        setHasMore(false);
      }

      const newBooks = await Promise.all(bookDocs.map(async (bookDoc) => {
        const bookData = bookDoc.data();
        
        // Get highlights for this book - Enhanced search with better error handling
        let highlights = [];
        if (bookDoc.id) {
          try {
            const highlightsRef = collection(db, 'highlights');
            
            // Enhanced highlight search with multiple fallback strategies
            const bookIdentifier = (bookData.isbn || '').split(' ')[0];
            
            // Strategy 1: Search by bookId and uid (most specific)
            if (bookIdentifier && bookData.uid) {
              try {
                const highlightsQuery = query(
                  highlightsRef,
                  where('bookId', '==', bookIdentifier),
                  where('uid', '==', bookData.uid),
                  orderBy('createdAt', 'desc'),
                  limit(15) // 더 많은 하이라이트를 가져와서 랜덤 선택 가능하게
                );
                const snapshot = await getDocs(highlightsQuery);
                highlights = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log(`Found ${highlights.length} highlights for book ${bookDoc.id} using bookId + uid: ${bookIdentifier}`);
              } catch (error) {
                console.warn('Failed to fetch highlights with bookId + uid:', bookIdentifier, error);
              }
            }
            
            // Strategy 2: Search by bookDocId (Firestore document ID)
            if (highlights.length === 0) {
              try {
                const highlightsQuery = query(
                  highlightsRef,
                  where('bookDocId', '==', bookDoc.id),
                  orderBy('createdAt', 'desc'),
                  limit(15)
                );
                const snapshot = await getDocs(highlightsQuery);
                highlights = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log(`Found ${highlights.length} highlights for book ${bookDoc.id} using bookDocId`);
              } catch (error) {
                console.warn('Failed to fetch highlights with bookDocId:', bookDoc.id, error);
              }
            }
            
            // Strategy 3: Search by bookId only (fallback for public highlights)
            if (highlights.length === 0 && bookIdentifier) {
              try {
                const highlightsQuery = query(
                  highlightsRef,
                  where('bookId', '==', bookIdentifier),
                  orderBy('createdAt', 'desc'),
                  limit(15)
                );
                const snapshot = await getDocs(highlightsQuery);
                highlights = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log(`Found ${highlights.length} highlights for book ${bookDoc.id} using bookId only`);
              } catch (error) {
                console.warn('Failed to fetch highlights with bookId only:', bookIdentifier, error);
              }
            }
            
            // Filter out highlights with empty or invalid text
            highlights = highlights.filter(highlight => {
              const text = highlight.text || '';
              return text.trim().length > 0 && text.trim().length < 1000; // 너무 긴 텍스트 제외
            });
            
          } catch (error) {
            console.warn('Failed to fetch highlights for book:', bookDoc.id, error);
          }
        }

        // Get user info
        let userName = '익명';
        if (bookData.uid) {
          try {
            const userDoc = await getDoc(doc(db, 'users', bookData.uid));
            if (userDoc.exists()) {
              userName = userDoc.data().displayName || '익명';
            }
          } catch (error) {
            console.warn('Failed to fetch user info for uid:', bookData.uid, error);
          }
        }

        // Get tags from memo-edit (최대 3개)
        let tags = [];
        if (bookData.tags && Array.isArray(bookData.tags)) {
          tags = bookData.tags.slice(0, 3); // 최대 3개만 표시
        }

        return {
          id: bookDoc.id,
          bookTitle: bookData.title || '제목 없음',
          bookThumbnail: bookData.thumbnail || '/images/no-cover.png',
          memo: bookData.memo || '',
          bookDocId: bookDoc.id,
          uid: bookData.uid || '',
          userName: userName,
          highlights: Array.isArray(highlights) ? highlights : [],
          tags: tags,
          createdAt: bookData.createdAt,
          isbn: bookData.isbn || '',
          bookId: (bookData.isbn || '').split(' ')[0] || '' // BookDetail.jsx와 동일한 bookId
        };
      }));

      // 모든 책을 포함하되, 하이라이트가 있는 책을 우선적으로 표시
      const booksWithHighlights = newBooks.filter(book => book.highlights.length > 0);
      const booksWithoutHighlights = newBooks.filter(book => book.highlights.length === 0);
      
      // 하이라이트가 있는 책들을 우선적으로 처리
      let selectedBooks = booksWithHighlights;
      
      // 하이라이트가 있는 책이 충분하지 않으면 하이라이트가 없는 책도 포함
      if (selectedBooks.length < 5 && booksWithoutHighlights.length > 0) {
        selectedBooks = [...selectedBooks, ...booksWithoutHighlights.slice(0, 5 - selectedBooks.length)];
      }
      
      if (selectedBooks.length === 0) {
        console.log('No books found in this batch');
        if (isInitial) {
          setBookCards([]);
        }
        setLoadingMore(false);
        return;
      }
      
      // 중복 방지: 이미 로드된 책 ID 제외
      const uniqueBooks = selectedBooks.filter(book => !loadedBookIds.has(book.id));
      
      if (uniqueBooks.length === 0) {
        console.log('All books in this batch are already loaded');
        setHasMore(false);
        setLoadingMore(false);
        return;
      }
      
      // 랜덤 정렬 (초기 로드 시에만)
      const processedBooks = isInitial ? uniqueBooks.sort(() => 0.5 - Math.random()) : uniqueBooks;
      
      // 로드된 책 ID들을 Set에 추가
      const newBookIds = new Set(processedBooks.map(book => book.id));
      setLoadedBookIds(prev => new Set([...prev, ...newBookIds]));
      
      // 디버깅을 위한 로그
      console.log('Loaded book cards:', processedBooks.map(book => ({
        id: book.id,
        title: book.bookTitle,
        highlightsCount: book.highlights.length,
        bookId: book.bookId,
        isbn: book.isbn,
        uid: book.uid,
        tags: book.tags
      })));
      
      if (isInitial) {
        setBookCards(processedBooks);
        setIsInitialLoad(false);
      } else {
        setBookCards(prev => [...prev, ...processedBooks]);
      }
    } catch (error) {
      console.error('책 카드 로드 오류:', error);
    } finally {
      setLoadingMore(false);
      setLoading(false);
    }
  }, [lastDoc, hasMore, loadingMore, loadedBookIds, isInitialLoad]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        p: 4,
        cursor: 'default'
      }}>
        <CircularProgress size={28} sx={{ color: 'rgba(0, 0, 0, 0.4)' }} />
      </Box>
    );
  }

  if (bookCards.length === 0) {
    return (
      <Box sx={{ px: 2, textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ 
          color: '#666', 
          mb: 2,
          cursor: 'default',
          userSelect: 'none'
        }}>
          아직 기록된 책이 없습니다
        </Typography>
        {currentUser ? (
          <Button
            variant="contained"
            onClick={() => navigate('/search', { state: { focusSearch: true } })}
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#333',
              color: 'white',
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: '#000',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              '&:focus': {
                outline: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2), 0 0 0 2px rgba(0,0,0,0.1)',
              }
            }}
          >
            첫 번째 책 추가하기
          </Button>
        ) : (
          <Typography variant="body2" sx={{ color: '#999', fontSize: '14px' }}>
            로그인하여 독서 기록을 시작해보세요
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ 
      px: 2,
      position: 'relative',
      zIndex: 1,
      pointerEvents: 'auto'
    }}>
      {bookCards.map((bookData) => (
        <EnhancedBookCard
          key={bookData.id}
          bookData={bookData}
          currentUser={currentUser}
          navigate={navigate}
        />
      ))}
      
      {loadingMore && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          py: 2,
          cursor: 'default',
          position: 'relative',
          zIndex: 1
        }}>
          <CircularProgress size={24} sx={{ color: 'rgba(0, 0, 0, 0.4)' }} />
        </Box>
      )}
      
      {!hasMore && bookCards.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          py: 3,
          cursor: 'default',
          position: 'relative',
          zIndex: 1
        }}>
          <Typography
            variant="body2"
            sx={{
              color: '#999',
              fontSize: '14px',
              fontFamily: 'NanumSquareR',
              textAlign: 'center'
            }}
          >
            모든 독서 기록을 확인했습니다
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// --- NEW Compact Book List Component ---
const CompactBookList = ({ title, books, currentUser, navigate, loading }) => {
  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ px: 2, mb: 2, fontWeight: '700', fontFamily: 'NanumSquareEB', fontSize: '18px', color: '#333' }}>
          {title}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          py: 1,
          cursor: 'default'
        }}>
          <CircularProgress size={20} sx={{ color: 'rgba(0, 0, 0, 0.4)' }} />
        </Box>
      </Box>
    );
  }

  if (!books || books.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ px: 2, mb: 2, fontWeight: '700', fontFamily: 'NanumSquareEB', fontSize: '18px', color: '#333' }}>
          {title}
        </Typography>
        <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ 
            color: '#999', 
            fontSize: '14px',
            cursor: 'default',
            userSelect: 'none'
          }}>
            {title === 'Reading' ? '읽고 있는 책이 없습니다' : '읽을 책이 없습니다'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      mb: 3,
      position: 'relative',
      zIndex: 50,
      pointerEvents: 'auto'
    }}>
      <Typography sx={{ 
        px: 2, 
        mb: 0.5, 
        fontWeight: '700', 
        fontFamily: 'NanumSquareEB', 
        fontSize: '18px', 
        color: '#333',
        cursor: 'default',
        userSelect: 'none',
        position: 'relative',
        zIndex: 51,
        pointerEvents: 'none'
      }}>
        {title}
      </Typography>
      <Box sx={{ 
        px: 2,
        display: 'flex', 
        gap: 2, 
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        pb: 1,
        position: 'relative',
        zIndex: 50,
        pointerEvents: 'auto'
      }}>
        {books.slice(0, 5).map((book) => (
          <Box
            key={book.id}
            onClick={() => navigate(`/book/${book.id}?uid=${currentUser.uid}`)}
            sx={{
              width: '120px',
              height: '120px',
              flexShrink: 0,
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              cursor: 'pointer',
              backgroundImage: `url(${book.thumbnail || '/images/no-cover.png'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '0 6px 24px rgba(0,0,0,0.18), 0 3px 12px rgba(0,0,0,0.12)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-8px) scale(1.06)',
                boxShadow: '0 16px 40px rgba(0,0,0,0.28), 0 8px 20px rgba(0,0,0,0.18)',
              },
              '&:active': {
                transform: 'translateY(-4px) scale(1.03)',
              }
            }}
          >
            {/* 검은색 불투명 레이어 */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {/* 책 제목 */}
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 800,
                  textAlign: 'center',
                  lineHeight: 1.3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                  fontFamily: 'NanumSquareEB',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                }}
              >
                {book.title || '제목 없음'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// --- NEW RecentReadingCard Component ---
const RecentReadingCard = ({ bookData, currentUser, navigate }) => {
  const handleNavigation = () => {
    const bookDocId = bookData.bookDocId;
    const userUid = currentUser?.uid;
    if (bookDocId && bookDocId.includes('_') && userUid) {
      navigate(`/book/${bookDocId}?uid=${userUid}`);
    } else {
      console.warn('[Home - RecentReadingCard] Invalid data for navigation:', { bookDocId, userUid }, bookData);
    }
  };

  return (
    <Paper 
      onClick={handleNavigation}
      elevation={0}
      sx={{
        position: 'relative', 
        height: '100%', 
        minHeight: 252, // 10% 감소 (280 -> 252)
        width: '100%', 
        boxSizing: 'border-box', // 추가: 너비 계산에 패딩과 테두리 포함
        borderRadius: '12px', // Keep established radius
        overflow: 'hidden', 
        cursor: 'pointer',
        bgcolor: 'grey.800', // Fallback
        backgroundImage: bookData.bookThumbnail ? `url(${bookData.bookThumbnail})` : 'none', 
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'scale(1.03)'
        },
      }}
    >
      {/* Dark Overlay */}
      <Box sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: 'rgba(0, 0, 0, 0.5)', // Updated overlay opacity
        zIndex: 0,
        borderRadius: 'inherit' 
      }} />

      {/* Tag - Remains positioned top-left */}
      <Typography 
         variant="caption" 
         sx={{ 
            position: 'absolute', 
            top: 12, 
            left: 12, 
            zIndex: 2, 
            display: 'inline-block', 
            fontWeight: '600', 
            fontSize: '12px', 
            color: 'rgba(255, 255, 255, 1)', 
            px: '6px', 
            py: '2px', 
            border: '1px solid rgba(255, 255, 255, 0.8)',
            borderRadius: '12px', 
          }}>
         {bookData.type}
      </Typography>

      {/* Main Content Wrapper - Spans overlay, uses flex column and spacer */}
      <Box sx={{
        position: 'absolute', 
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1, 
        // Apply padding: Top(16px), Sides(16px), Bottom(Increased to 20px)
        px: 2, 
        pt: 2, 
        pb: 2.5, // Increased padding-bottom for more space below text
        display: 'flex', 
        flexDirection: 'column', 
        color: 'white',
        boxSizing: 'border-box', // 추가: 너비 계산에 패딩과 테두리 포함
      }}>
        {/* Bottom Text Block - Now positioned near the top */} 
        <Box sx={{ width: '100%', mt: 4, boxSizing: 'border-box' }}> {/* Add margin-top to position below the tag */}
          {/* Title */} 
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: '700', 
              fontSize: '20px', // 폰트 크기 약간 축소
              lineHeight: 1.3,
              textShadow: '1px 1px 3px rgba(0,0,0,0.5)', 
              textAlign: 'left', 
              display: '-webkit-box',
              WebkitLineClamp: 2, 
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              maxHeight: '65px', // 최대 높이 약간 증가
              height: 'auto',
              width: '100%', // 너비 명시
              px: 0.2,
              mb: 0.5,
              boxSizing: 'border-box', // 너비 계산에 패딩 포함
            }}
          >
            {bookData.bookTitle}
          </Typography>
          
          {/* Sub-info */} 
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '13px', // 폰트 크기 약간 축소
              opacity: 0.9,
              color: 'rgba(255, 255, 255, 0.85)', 
              textShadow: '1px 1px 2px rgba(0,0,0,0.4)',
              textAlign: 'left', 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              width: '100%', // 너비 명시
              boxSizing: 'border-box' // 너비 계산에 패딩 포함
            }}
          >
            {bookData.type === 'Recent Highlight' && `Last highlight: ${formatRelativeTime(bookData.lastHighlightDate)}`}
            {bookData.type === 'Revisit' && `Last highlight: ${formatRelativeTime(bookData.lastHighlightDate)}`}
            {bookData.type === 'Most Highlighted' && `${bookData.highlights.length} highlight${bookData.highlights.length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
// --- END RecentReadingCard Component ---

// --- NEW BookReviewCard Component ---
const BookReviewCard = ({ review, currentUser, navigate, createClickHandler }) => {
  const handleNavigation = () => {
    const bookDocId = review.bookDocId || review.bookDocIdForNav;
    const userUid = review.uid;
    if (bookDocId && userUid) {
      navigate(`/book/${bookDocId}?uid=${userUid}`);
    } else {
      console.warn('[Home - BookReviewCard] Invalid data for navigation:', { bookDocId, userUid }, review);
    }
  };

  // 유저가 직접 등록한 이미지(userThumbnail) 우선 적용
  const backgroundImg = review.userThumbnail || review.bookThumbnail || '/images/no-cover.png';
  // 썸네일(작은 이미지)은 기존 ISBN 기반 이미지만 사용
  const thumbImg = review.bookThumbnail || '/images/no-cover.png';

  return (
    <Paper
      onClick={createClickHandler(handleNavigation)}
      elevation={0}
      sx={{
        width: { xs: '227px', sm: '259px', md: '292px' },
        height: { xs: '324px', sm: '356px', md: '389px' },
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        bgcolor: 'grey.800',
        backgroundImage: `url(${backgroundImg})`,
        backgroundSize: review.userThumbnail ? '100% auto' : 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: review.userThumbnail ? '#222' : 'grey.800',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.2), 0 8px 24px rgba(0,0,0,0.12)',
        },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { xs: 1.5, sm: 2 },
        boxSizing: 'border-box',
      }}
    >
      {/* Gradient & Blur Overlay (유저 이미지일 때만 블러/어둡게) */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        background: review.userThumbnail
          ? 'linear-gradient(to bottom, rgba(0,0,0,0.7) 10%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.50) 100%)'
          : 'linear-gradient(to bottom, rgba(0,0,0,0.7) 10%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.90) 100%)',
        backdropFilter: review.userThumbnail ? 'blur(0px)' : 'none',
      }} />
      {/* Top Area: Memo Text */}
                <Typography
            sx={{
              color: 'white',
              fontSize: { xs: '21px', sm: '23px', md: '25px' },
              lineHeight: 1.6,
              fontWeight: 700,
              display: '-webkit-box',
              WebkitLineClamp: 5,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              zIndex: 2,
              textShadow: '0 2px 8px rgba(0,0,0,0.6)',
              fontFamily: 'NanumSquareBold',
              letterSpacing: '-0.02em',
            }}
          >
        {review.text}
      </Typography>
      {/* Bottom Area */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 2,
        zIndex: 2,
      }}>
        {/* Left Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          flexGrow: 1,
          minWidth: 0,
        }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: { xs: '13px', sm: '14px' }, fontWeight: 400 , mt: '2px' }}>
            {review.userName} 님의
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'white',
              fontSize: { xs: '15px', sm: '16px' },
              fontWeight: 700,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.4,
              minHeight: '2.8em',
            }}
          >
            {review.bookTitle}
          </Typography>
          {review.highlightCount !== null && (
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: { xs: '12px', sm: '13px' }, minHeight: '1.2em' }}>
              {`${review.highlightCount} 개의 문장`}
            </Typography>
          )}
        </Box>
        {/* Right Section (Book Cover) */}
        <Box sx={{
          width: { xs: '56px', sm: '64px', md: '72px' },
          height: 'auto',
          aspectRatio: '2 / 3',
          borderRadius: '8px',
          overflow: 'hidden',
          flexShrink: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}>
          <img
            src={thumbImg}
            alt={review.bookTitle}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

// --- END BookReviewCard Component ---

// --- BookReviewCards Container Component ---
const BookReviewCards = ({ currentUser, navigate, followingIds, authLoading }) => {
  const [reviewCards, setReviewCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const containerRef = useRef(null);
  const autoScrollTimeoutRef = useRef(null);
  const autoScrollCount = useRef(0);
  const userInteracted = useRef(false);
  
  // 랜덤 책 커버 이미지 선택 (성능 최적화)
  const randomBookCovers = useMemo(() => {
    const images = ['home-01.png', 'home-02.png', 'home-03.png', 'home-04.png', 'home-05.png', 'home-06.png', 'home-07.png', 'home-08.png'];
    return [...images].sort(() => 0.5 - Math.random()).slice(0, 4);
  }, []);
  
  // 책 추가하기 버튼용 랜덤 이미지 선택 (성능 최적화)
  const randomAddBookImage = useMemo(() => {
    const images = ['home-01.png', 'home-02.png', 'home-03.png', 'home-04.png', 'home-05.png', 'home-06.png', 'home-07.png', 'home-08.png'];
    return images[Math.floor(Math.random() * images.length)];
  }, []);
  
  const isDragging = useRef(false);
  const wasDragged = useRef(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // 최초 1회만 북리뷰카드 로딩 (실시간 구독 제거)
  useEffect(() => {
    if (!authLoading) {
      loadReviewCards(true);
    }
    // 최초 1회만 실행 (페이지 마운트 시)
    // 이후 reviewCards는 새 배열로 반복 갱신하지 않음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  // loadReviewCards 내부에서 onSnapshot 등 실시간 구독 제거, getDocs로만 fetch
  const loadReviewCards = useCallback(async (isInitial = false) => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const booksRef = collection(db, 'books');
      let bookDocs = [];
      if (currentUser) {
        // 로그인: 내 책/팔로잉 책을 최초 1회만 getDocs로 fetch
        const queryConstraints = [
          where("memo", ">", ""),
          orderBy("memo"),
          orderBy("createdAt", "desc"),
          limit(10)
        ];
        const allIds = [currentUser.uid, ...(followingIds || [])];
        let q = query(booksRef, where('uid', 'in', allIds.length > 0 ? allIds : [currentUser.uid]), ...queryConstraints);
        if (!isInitial && lastDoc) {
          q = query(q, startAfter(lastDoc));
        }
        const snapshot = await getDocs(q);
        bookDocs = snapshot.docs;
        if (bookDocs.length < 10) {
          setHasMore(false);
        }
        setLastDoc(bookDocs[bookDocs.length - 1]);
      } else {
        // 비로그인: 기존과 동일
        const publicBooksQuery = query(booksRef, orderBy("createdAt", "desc"), limit(50));
        const snapshot = await getDocs(publicBooksQuery);
        const allBooksWithMemo = snapshot.docs.filter(doc => doc.data().memo && doc.data().memo.trim() !== "");
        const shuffled = allBooksWithMemo.sort(() => 0.5 - Math.random());
        bookDocs = shuffled.slice(0, 10);
        setHasMore(false);
      }
      const newReviews = bookDocs.map((bookDoc) => {
        const bookData = bookDoc.data();
        return {
          id: bookDoc.id,
          text: bookData.memo,
          bookTitle: bookData.title,
          bookThumbnail: bookData.thumbnail,
          userThumbnail: bookData.userThumbnail || '',
          bookDocId: bookDoc.id,
          uid: bookData.uid,
          isbn: bookData.isbn,
          userName: '...',
          highlightCount: null,
        };
      });
      // 북리뷰카드 리스트를 랜덤하게 섞기 (작성일 내림차순이 아닌 랜덤)
      const shuffledReviews = newReviews.sort(() => 0.5 - Math.random());
      if (isInitial) {
        setReviewCards(shuffledReviews);
      } else {
        setReviewCards(prev => [...prev, ...shuffledReviews]);
      }
    } catch (error) {
      console.error('책 메모 카드 로드 오류:', error);
      if (error.code === 'failed-precondition') {
        console.error("Firestore 인덱스가 필요합니다. 콘솔의 링크를 클릭하여 인덱스를 생성해주세요.");
      }
    } finally {
      setLoadingMore(false);
      setLoading(false);
    }
  }, [currentUser, followingIds, lastDoc, hasMore, loadingMore]);

  const handleMouseDown = useCallback((e) => {
    userInteracted.current = true;
    if (autoScrollTimeoutRef.current) clearTimeout(autoScrollTimeoutRef.current);
    if (!containerRef.current) return;
    
    isDragging.current = true;
    wasDragged.current = false;
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    
    containerRef.current.style.cursor = 'grabbing';
    containerRef.current.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || !containerRef.current) return;
    e.preventDefault();
    wasDragged.current = true;
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  }, [startX, scrollLeft]);

  const handleMouseUpAndLeave = useCallback(() => {
    isDragging.current = false;
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
      containerRef.current.style.userSelect = 'auto';
    }
  }, []);

  const createClickHandler = (action) => (e) => {
    if (wasDragged.current) {
      e.preventDefault();
      return;
    }
    action();
  };

  // Auto-scroll animation on initial load
  useEffect(() => {
    // Stop if animation is not ready, has already run, or user has interacted
    if (userInteracted.current || authLoading || loading || reviewCards.length < 2 || !containerRef.current) {
      return;
    }

    const container = containerRef.current;

    const doAutoScroll = () => {
      // Stop conditions: scrolled enough times, user interacted, or component unmounted
      if (autoScrollCount.current >= 6 || userInteracted.current || !containerRef.current) {
        return;
      }
      
      const children = Array.from(container.children);
      const cardToScrollPast = children[autoScrollCount.current];

      if (!cardToScrollPast) {
        return; // No more cards to scroll past
      }
      
      const gap = parseFloat(window.getComputedStyle(container).gap) || 16;
      const scrollAmount = container.scrollLeft + cardToScrollPast.offsetWidth + gap;
      
      container.scrollTo({
        left: scrollAmount,
        behavior: 'smooth',
      });
      
      autoScrollCount.current += 1;

      // Schedule the next scroll if we haven't reached the limit
      if (autoScrollCount.current < 7) {
        autoScrollTimeoutRef.current = setTimeout(doAutoScroll, 2000);
      }
    };

    const cancelAutoScroll = () => {
      userInteracted.current = true;
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
      container.removeEventListener('wheel', cancelAutoScroll);
      container.removeEventListener('touchstart', cancelAutoScroll);
    };

    container.addEventListener('wheel', cancelAutoScroll, { once: true });
    container.addEventListener('touchstart', cancelAutoScroll, { once: true });

    // Start the first scroll after a short delay
    autoScrollTimeoutRef.current = setTimeout(doAutoScroll, 500);

    return () => {
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
      if (container) {
        container.removeEventListener('wheel', cancelAutoScroll);
        container.removeEventListener('touchstart', cancelAutoScroll);
      }
    };
  }, [authLoading, loading, reviewCards.length]);

  // Scroll handler for lazy loading
  const handleScroll = useCallback(
    debounce(() => {
      if (!containerRef.current || !hasMore || loadingMore) return;

      const container = containerRef.current;
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      if (scrollLeft + clientWidth >= scrollWidth * 0.8) {
        loadReviewCards();
      }
    }, 200),
    [hasMore, loadingMore, loadReviewCards]
  );

  // Add scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // 작성자 정보(userName) 비동기 fetch (무한루프/무한리퀘스트 방지)
  useEffect(() => {
    const cardsToUpdate = reviewCards.filter(card => card.userName === '...');
    if (cardsToUpdate.length === 0) return;

    Promise.all(cardsToUpdate.map(card =>
      getDoc(doc(db, 'users', card.uid)).then(snap =>
        snap.exists() ? { id: card.id, userName: snap.data().displayName || '익명' } : { id: card.id, userName: '익명' }
      )
    )).then(results => {
      setReviewCards(prev =>
        prev.map(card => {
          const found = results.find(r => r.id === card.id);
          return found ? { ...card, userName: found.userName } : card;
        })
      );
    });
  }, [reviewCards]);

  return (
    <Box sx={{ mb: 4 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: { xs: '360px', sm: '396px', md: '432px' } }}>
          <CircularProgress size={28} sx={{ color: 'rgba(0, 0, 0, 0.4)' }} />
        </Box>
      ) : reviewCards.length > 0 ? (
        <Box
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUpAndLeave}
          onMouseLeave={handleMouseUpAndLeave}
                      sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: { xs: 2, sm: 2.5, md: 3 },
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              pb: 2,
              pr: { xs: 2, sm: 3 },
              pl: { xs: 2, sm: 3 },
              scrollBehavior: 'smooth',
              cursor: 'grab',
            }}
        >
          {currentUser && (
            <Paper
              elevation={0}
              onClick={createClickHandler(() => navigate('/search', { state: { focusSearch: true } }))}
              sx={{
                width: { xs: '227px', sm: '259px', md: '292px' },
                height: { xs: '324px', sm: '356px', md: '389px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '20px',
                cursor: 'pointer',
                flexShrink: 0,
                boxSizing: 'border-box',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderColor: 'rgba(0, 0, 0, 0.2)',
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              {/* 배경 이미지와 블러 효과 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(/images/${randomAddBookImage})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  filter: 'blur(4px)',
                  opacity: 0.8,
                  zIndex: 0,
                }}
              />
              
              {/* 그라데이션 오버레이 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.75) 100%)',
                  zIndex: 1,
                }}
              />
              
                              {/* 메인 콘텐츠 */}
                <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <AddCircleOutlineIcon sx={{ 
                    fontSize: 40, 
                    color: '#333', 
                    mb: 1,
                  }} />
                  <Typography variant="button" color="text.secondary" sx={{ 
                    fontSize: '20px', 
                    fontWeight: 700,
                    color: 'black',
                    fontFamily: 'NanumSquareBold',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.1)'
                  }}>
                    책 추가하기
                  </Typography>
                </Box>
            </Paper>
          )}

          {reviewCards.map((review) => (
            <BookReviewCard
              key={review.id}
              review={review}
              currentUser={currentUser}
              createClickHandler={createClickHandler}
              navigate={navigate}
            />
          ))}
          
          {loadingMore && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              width: { xs: '227px', sm: '259px', md: '292px' }, 
              height: { xs: '324px', sm: '356px', md: '389px' }, 
              flexShrink: 0 
            }}>
              <CircularProgress size={28} sx={{ color: 'rgba(0, 0, 0, 0.4)' }} />
            </Box>
          )}
        </Box>
      ) : (
         <Paper elevation={0} sx={{ 
           p: 0, 
           textAlign: 'center', 
           borderRadius: 3, 
           backgroundColor: 'transparent', 
           minHeight: { xs: '360px', sm: '396px', md: '432px' }, 
           display: 'flex', 
           alignItems: 'center', 
           justifyContent: 'center', 
           flexDirection: 'column',
           overflow: 'hidden',
           position: 'relative'
         }}>
             {currentUser ? (
               <Box sx={{ 
                 display: 'flex', 
                 flexDirection: 'column', 
                 alignItems: 'center', 
                 gap: 3,
                 position: 'relative',
                 zIndex: 2,
                 width: '100%',
                 height: '100%',
                 justifyContent: 'center',
                 p: 3
               }}>
                                   {/* 랜덤 책 커버 이미지들 */}
                  <Box sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    opacity: 0.55,
                    transform: 'rotate(-5deg)',
                    filter: 'blur(1.5px)'
                  }}>
                    {randomBookCovers.map((img, index) => (
                                              <Box
                          key={img}
                          component="img"
                          src={`/images/${img}`}
                          alt="SnapLog 독서기록 배경 이미지"
                          loading="lazy"
                          sx={{
                            width: { xs: 75, sm: 100 },
                            height: { xs: 112.5, sm: 150 },
                            objectFit: 'cover',
                            borderRadius: 1,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            transform: `rotate(${(index - 1.5) * 8}deg)`,
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                              transform: `rotate(${(index - 1.5) * 8}deg) scale(1.05)`,
                            }
                          }}
                        />
                    ))}
                  </Box>

                 {/* 텍스트 가독성을 위한 배경 오버레이 */}
                 <Box sx={{
                   position: 'absolute',
                   top: '50%',
                   left: '50%',
                   transform: 'translate(-50%, -50%)',
                   width: '100%',
                   height: '100%',
                   background: 'radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.6) 70%, rgba(255,255,255,0.3) 100%)',
                   borderRadius: 2,
                   zIndex: 1
                 }} />

                 {/* 메인 콘텐츠 */}
                 <Box sx={{ 
                   display: 'flex', 
                   flexDirection: 'column', 
                   alignItems: 'center', 
                   gap: 1,
                   maxWidth: '280px',
                   position: 'relative',
                   zIndex: 3
                 }}>
                   <Typography 
                     sx={{ 
                       fontWeight: 700,
                       fontSize: { xs: '1.4rem', sm: '1.5rem' },
                       color: '#111',
                       lineHeight: 1.4,
                       textAlign: 'center',
                       position: 'relative',
                       zIndex: 2,
                       fontFamily: 'NanumSquareEB',
                       textShadow: '0 2px 4px rgba(255, 255, 255, 0.8)'
                     }}
                   >
                     당신의 기록도 추가해보세요
                   </Typography>
                   
                   <Typography 
                     color="text.secondary" 
                     variant="body2" 
                     sx={{ 
                       fontSize: { xs: '1rem', sm: '1.1rem' },
                       color: '#666',
                       mb: 2,
                       lineHeight: 1.5,
                       textAlign: 'center',
                       position: 'relative',
                       zIndex: 2,
                       fontFamily: 'NanumSquareB'
                     }}
                   >
                     읽은 책을 모두 기록해 보세요
                   </Typography>

                   <Button
                     variant="contained"
                     onClick={() => navigate('/search', { state: { focusSearch: true } })}
                     sx={{
                       fontWeight: 700,
                       fontSize: { xs: '1rem', sm: '1.1rem' },
                       color: '#fff',
                       background: '#111',
                       borderRadius: 3,
                       px: 4,
                       py: 1.5,
                       minWidth: 0,
                       boxShadow: '0 8px 25px rgba(17, 17, 17, 0.3)',
                       display: 'flex',
                       alignItems: 'center',
                       gap: 1,
                       transition: 'all 0.3s ease-in-out',
                       textTransform: 'none',
                       position: 'relative',
                       zIndex: 2,
                       fontFamily: 'NanumSquareEB',
                       '&:hover': {
                         background: '#000',
                         boxShadow: '0 12px 35px rgba(17, 17, 17, 0.4)',
                         transform: 'translateY(-2px)',
                       },
                       '&:active': {
                         transform: 'translateY(0px)',
                       }
                     }}
                   >
                     <AddIcon sx={{ fontSize: '1.2rem' }} />
                     책 추가하기
                   </Button>
                 </Box>
               </Box>
             ) : (
               <Box sx={{ 
                 display: 'flex', 
                 flexDirection: 'column', 
                 alignItems: 'center', 
                 gap: 2,
                 p: 3
               }}>
                 <Typography 
                   variant="h6" 
                   sx={{ 
                     fontWeight: 600,
                     fontSize: { xs: '1.1rem', sm: '1.25rem' },
                     color: '#333',
                     mb: 1
                   }}
                 >
                   아직 작성된 메모가 없습니다
                 </Typography>
                 <Typography 
                   color="text.secondary" 
                   variant="body2" 
                   sx={{ 
                     fontSize: { xs: '0.9rem', sm: '1rem' },
                     color: '#666',
                     textAlign: 'center'
                   }}
                 >
                   로그인하고 첫 번째 독서 기록을 남겨보세요
                 </Typography>
               </Box>
             )}
         </Paper>
      )}
    </Box>
  );
};

// --- END BookReviewCards Component ---

// --- CurrentlyReadingBooks Component ---
const CurrentlyReadingBooks = ({ currentUser, navigate }) => {
  const [readingBooks, setReadingBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setReadingBooks([]);
      setLoading(false);
      setError(null);
      return;
    }

    console.log(`[CurrentlyReadingBooks] 사용자 ${currentUser.uid}의 읽는 중인 책 로딩 시작`);
    setLoading(true);
    setError(null);
    
    const booksRef = collection(db, 'books');
    
    // 더 안전한 쿼리: 인덱스 없이도 작동하는 방식
    let q;
    try {
      // 먼저 단순 쿼리로 시도 (인덱스 불필요)
      q = query(
        booksRef,
        where('uid', '==', currentUser.uid),
        limit(100) // 충분한 책을 가져와서 클라이언트에서 필터링
      );
    } catch (error) {
      console.error("쿼리 생성 오류:", error);
      setError("쿼리 생성 실패");
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        try {
          const allBooks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log(`[CurrentlyReadingBooks] 전체 책 ${allBooks.length}개 로드됨`);
          
          // 각 책의 readStatus 확인을 위한 디버깅
          allBooks.forEach(book => {
            console.log(`[CurrentlyReadingBooks] 책: ${book.title}, readStatus: ${book.readStatus}, uid: ${book.uid}`);
          });
          
          console.log(`[CurrentlyReadingBooks] 전체 책 중 readStatus 분포:`, 
            allBooks.reduce((acc, book) => {
              acc[book.readStatus || 'undefined'] = (acc[book.readStatus || 'undefined'] || 0) + 1;
              return acc;
            }, {})
          );
          
          // 모든 책의 상세 정보 로깅
          console.log(`[CurrentlyReadingBooks] 모든 책 상세 정보:`, allBooks.map(book => ({
            id: book.id,
            title: book.title,
            readStatus: book.readStatus,
            uid: book.uid,
            createdAt: book.createdAt
          })));
          
          // 클라이언트에서 정렬
          const sortedBooks = allBooks.sort((a, b) => {
            const dateA = a.createdAt?.toMillis?.() || 0;
            const dateB = b.createdAt?.toMillis?.() || 0;
            return dateB - dateA;
          });
          
          // 클라이언트에서 읽고 있는 책만 필터링 (memo-edit 상태 반영)
          const readingBooks = sortedBooks
            .filter(book => {
              // memo-edit에서 설정하는 읽는 중 상태 체크 (더 유연하게)
              const isReading = book.readStatus === 'reading' || 
                               book.readStatus === '읽는 중' ||
                               book.readStatus === '읽고 있음' ||
                               book.readStatus === '진행 중';
              
              if (isReading) {
                console.log(`[CurrentlyReadingBooks] 읽는 중인 책 발견: ${book.title} (readStatus: ${book.readStatus})`);
              }
              return isReading;
            })
            .slice(0, 5); // 최대 5개까지 표시
          
          console.log(`[CurrentlyReadingBooks] 읽는 중인 책 ${readingBooks.length}개 필터링됨`);
          setReadingBooks(readingBooks);
          setError(null);
        } catch (error) {
          console.error("읽고 있는 책 데이터 처리 오류:", error);
          setReadingBooks([]);
          setError("데이터 처리 오류");
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("읽고 있는 책 로딩 오류:", error);
        if (error.code === 'failed-precondition') {
          console.error("Firestore 인덱스가 필요합니다. 콘솔의 링크를 클릭하여 인덱스를 생성해주세요.");
        }
        // 오류 발생 시 빈 배열로 설정하여 UI가 깨지지 않도록 함
        setReadingBooks([]);
        setError("데이터 로딩 오류");
        setLoading(false);
      }
    );

    return () => {
      try {
        unsubscribe();
      } catch (error) {
        console.warn("구독 해제 중 오류:", error);
      }
    };
  }, [currentUser]);

  // 디버깅을 위한 조건부 렌더링 로그
  console.log(`[CurrentlyReadingBooks] 렌더링 조건 체크: currentUser=${!!currentUser}, readingBooks.length=${readingBooks.length}, loading=${loading}, error=${error}`);

  if (!currentUser) {
    console.log(`[CurrentlyReadingBooks] 사용자가 로그인하지 않음`);
    return null;
  }

  if (loading) {
    console.log(`[CurrentlyReadingBooks] 로딩 중...`);
    return null;
  }

  if (error) {
    console.log(`[CurrentlyReadingBooks] 오류 발생: ${error}`);
    return null;
  }

  if (readingBooks.length === 0) {
    console.log(`[CurrentlyReadingBooks] 읽는 중인 책이 없음`);
    return null;
  }

  return (
    <Box sx={{ mb: 2}}>
      <Typography sx={{ fontWeight: '700', fontFamily: 'NanumSquareEB', fontSize: '18px', color: '#333', px: 2, mb: 0 }}>
        Reading
      </Typography>
      <Box sx={{ 
        px: 2,
        display: 'flex', 
        gap: 2, 
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        pb: 1
      }}>
        {readingBooks.map((book) => (
          <Box
            key={book.id}
            onClick={() => navigate(`/book/${book.id}?uid=${currentUser.uid}`)}
            sx={{
              width: '80px',
              height: '80px',
              flexShrink: 0,
              position: 'relative',
              borderRadius: '16px',
              overflow: 'hidden',
              cursor: 'pointer',
              backgroundImage: `url(${book.thumbnail || '/images/no-cover.png'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-6px) scale(1.05)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.25), 0 6px 16px rgba(0,0,0,0.15)',
              },
              '&:active': {
                transform: 'translateY(-3px) scale(1.02)',
              }
            }}
          >
            {/* Dark Overlay */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.9) 100%)',
              zIndex: 1,
            }} />
            
            {/* Book Title */}
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 1,
              zIndex: 2,
            }}>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: 800,
                  lineHeight: 1.2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                  fontFamily: 'NanumSquareEB',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  textAlign: 'center',
                }}
              >
                {book.title}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
// --- END CurrentlyReadingBooks Component ---

// --- CurrentlyReadingBooks Component ---
const NotStartedBooks = ({ currentUser, navigate }) => {
  const [notStartedBooks, setNotStartedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setNotStartedBooks([]);
      setLoading(false);
      setError(null);
      return;
    }

    console.log(`[NotStartedBooks] 사용자 ${currentUser.uid}의 읽기 전 책 로딩 시작`);
    setLoading(true);
    setError(null);
    
    const booksRef = collection(db, 'books');
    
    // 더 안전한 쿼리: 인덱스 없이도 작동하는 방식
    let q;
    try {
      // 먼저 단순 쿼리로 시도 (인덱스 불필요)
      q = query(
        booksRef,
        where('uid', '==', currentUser.uid),
        limit(100) // 충분한 책을 가져와서 클라이언트에서 필터링
      );
    } catch (error) {
      console.error("쿼리 생성 오류:", error);
      setError("쿼리 생성 실패");
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        try {
          const allBooks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log(`[NotStartedBooks] 전체 책 ${allBooks.length}개 로드됨`);
          
          // 각 책의 readStatus 확인을 위한 디버깅
          allBooks.forEach(book => {
            console.log(`[NotStartedBooks] 책: ${book.title}, readStatus: ${book.readStatus}, uid: ${book.uid}`);
          });
          
          console.log(`[NotStartedBooks] 전체 책 중 readStatus 분포:`, 
            allBooks.reduce((acc, book) => {
              acc[book.readStatus || 'undefined'] = (acc[book.readStatus || 'undefined'] || 0) + 1;
              return acc;
            }, {})
          );
          
          // 모든 책의 상세 정보 로깅
          console.log(`[NotStartedBooks] 모든 책 상세 정보:`, allBooks.map(book => ({
            id: book.id,
            title: book.title,
            readStatus: book.readStatus,
            uid: book.uid,
            createdAt: book.createdAt
          })));
          
          // 클라이언트에서 정렬
          const sortedBooks = allBooks.sort((a, b) => {
            const dateA = a.createdAt?.toMillis?.() || 0;
            const dateB = b.createdAt?.toMillis?.() || 0;
            return dateB - dateA;
          });
          
          // 클라이언트에서 읽기 전 책만 필터링 (memo-edit 상태 반영)
          const notStartedBooks = sortedBooks
            .filter(book => {
              // memo-edit에서 설정하는 읽기 전 상태 체크 (더 유연하게)
              const isNotStarted = book.readStatus === 'not_started' || 
                                  book.readStatus === '읽기 전' ||
                                  book.readStatus === '읽지 않음' ||
                                  book.readStatus === '미읽' ||
                                  book.readStatus === '대기 중' ||
                                  book.readStatus === undefined || 
                                  book.readStatus === null;
              
              if (isNotStarted) {
                console.log(`[NotStartedBooks] 읽기 전 책 발견: ${book.title} (readStatus: ${book.readStatus})`);
              }
              return isNotStarted;
            })
            .slice(0, 5); // 최대 5개까지 표시
          
          console.log(`[NotStartedBooks] 읽기 전 책 ${notStartedBooks.length}개 필터링됨`);
          setNotStartedBooks(notStartedBooks);
          setError(null);
        } catch (error) {
          console.error("읽기 전 책 데이터 처리 오류:", error);
          setNotStartedBooks([]);
          setError("데이터 처리 오류");
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("읽기 전 책 로딩 오류:", error);
        if (error.code === 'failed-precondition') {
          console.error("Firestore 인덱스가 필요합니다. 콘솔의 링크를 클릭하여 인덱스를 생성해주세요.");
        }
        // 오류 발생 시 빈 배열로 설정하여 UI가 깨지지 않도록 함
        setNotStartedBooks([]);
        setError("데이터 로딩 오류");
        setLoading(false);
      }
    );

    return () => {
      try {
        unsubscribe();
      } catch (error) {
        console.warn("구독 해제 중 오류:", error);
      }
    };
  }, [currentUser]);

  // 디버깅을 위한 조건부 렌더링 로그
  console.log(`[NotStartedBooks] 렌더링 조건 체크: currentUser=${!!currentUser}, notStartedBooks.length=${notStartedBooks.length}, loading=${loading}, error=${error}`);

  if (!currentUser) {
    console.log(`[NotStartedBooks] 사용자가 로그인하지 않음`);
    return null;
  }

  if (loading) {
    console.log(`[NotStartedBooks] 로딩 중...`);
    return null;
  }

  if (error) {
    console.log(`[NotStartedBooks] 오류 발생: ${error}`);
    return null;
  }

  if (notStartedBooks.length === 0) {
    console.log(`[NotStartedBooks] 읽기 전 책이 없음`);
    return null;
  }

  return (
    <Box sx={{ mb: 2}}>
      <Typography sx={{ fontWeight: '700', fontFamily: 'NanumSquareEB', fontSize: '18px', color: '#333', px: 2, mb: 0 }}>
        To Read
      </Typography>
      <Box sx={{ 
        px: 2,
        display: 'flex', 
        gap: 2, 
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        pb: 1
      }}>
        {notStartedBooks.map((book) => (
          <Box
            key={book.id}
            onClick={() => navigate(`/book/${book.id}?uid=${currentUser.uid}`)}
            sx={{
              width: '120px',
              height: '120px',
              flexShrink: 0,
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              cursor: 'pointer',
              backgroundImage: `url(${book.thumbnail || '/images/no-cover.png'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '0 6px 24px rgba(0,0,0,0.18), 0 3px 12px rgba(0,0,0,0.12)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-8px) scale(1.06)',
                boxShadow: '0 16px 40px rgba(0,0,0,0.28), 0 8px 20px rgba(0,0,0,0.18)',
              },
              '&:active': {
                transform: 'translateY(-4px) scale(1.03)',
              }
            }}
          >
            {/* Dark Overlay */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.9) 100%)',
              zIndex: 1,
            }} />
            
            {/* Book Title */}
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 1,
              zIndex: 2,
            }}>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 800,
                  lineHeight: 1.3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                  fontFamily: 'NanumSquareEB',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  textAlign: 'center',
                }}
              >
                {book.title}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
// --- END NotStartedBooks Component ---

// 하이라이트 카드 컴포넌트
const HighlightCard = ({ highlight, currentUser, navigate }) => {
  const isCurrentUser = currentUser && highlight.uid === currentUser.uid;
  // 작성자명 예외처리
  const displayName = isCurrentUser ? undefined : (highlight.userName || '익명');

  // 이동용 bookDocId 생성
  let bookDocId = highlight.bookDocIdForNav || highlight.bookDocId;
  if (!bookDocId && highlight.uid && highlight.bookId) {
    bookDocId = `${highlight.uid}_${highlight.bookId}`;
  }
  const userUid = highlight.uid;

  // 썸네일 이미지 경로
  const thumbnail = highlight.bookThumbnail || '/images/no-cover.png';

  // 개선된 클릭 핸들러
  const handleCardClick = () => {
    if (bookDocId && userUid) {
      // 스크롤 위치를 기억하기 위해 sessionStorage에 저장
      sessionStorage.setItem('scrollPosition', window.scrollY.toString());
      
      // 브라우저 호환성을 위한 전환 효과
      try {
        // 페이지 이동 시 부드러운 전환을 위한 클래스 추가
        document.body.style.transition = 'opacity 0.2s ease-out';
        document.body.style.opacity = '0.8';
      } catch (error) {
        console.warn('Transition effect failed:', error);
      }
      
      // 페이지 이동
      navigate(`/book/${bookDocId}?uid=${userUid}&highlightId=${highlight.id}`);
      
      // 전환 효과 복원
      setTimeout(() => {
        try {
          document.body.style.transition = '';
          document.body.style.opacity = '';
        } catch (error) {
          console.warn('Transition cleanup failed:', error);
        }
      }, 200);
    }
  };

  return (
    <Paper
      id={`highlight-${highlight.id}`}
      elevation={0}
      onClick={handleCardClick}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 'none',
        minWidth: 0,
        height: 252,
        bgcolor: '#F6F6F6',
        boxShadow: '0px 4px 8px rgba(0,0,0,0.10)',
        overflow: 'hidden', // 카드 내부만 보이게
        px: 0,
        py: 0,
        mb: 2,
        cursor: 'pointer',
        transition: 'box-shadow 0.18s, transform 0.2s ease-in-out', // transform 애니메이션 추가
        '&:hover': {
          boxShadow: '0px 8px 24px rgba(0,0,0,0.13)',
          transform: 'translateY(-2px)', // 호버 시 살짝 위로 이동
        },
        '&:active': {
          transform: 'translateY(0px) scale(0.98)', // 클릭 시 살짝 축소
        },
        display: 'block',
        boxSizing: 'border-box',
      }}
    >
      {/* 문장 텍스트 */}
      <Box
        sx={{
          // 썸네일+여백(58.83+16=74.83)만큼 오른쪽 패딩/최대너비 제한
          width: { xs: '100%', sm: '100%' },
          pt: { xs: '20px', sm: '28px' }, // 상단 여백
          pl: { xs: '16px', sm: '24px' }, // 좌측 여백
          pr: { xs: '16px', sm: '24px' }, // 썸네일+여백만큼 오른쪽 공간 확보
          boxSizing: 'border-box',
          // height: 66,
          zIndex: 2,
          fontFamily: 'NanumSquareB',
          fontWeight: 400,
          fontSize: 16,
          letterSpacing: '0.05em',
          lineHeight: '28px',
          color: '#000000',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 5,
          WebkitBoxOrient: 'vertical',
          background: 'transparent',
          // boxSizing은 위에서 이미 적용
        }}
      >
        {`${truncateText(highlight.text, 220)}`}
      </Box>

      {/* 하단 정보 그룹 */}
      <Box
        sx={{
          position: 'absolute',
          left: { xs: 16, sm: 24 },
          right: { xs: 90, sm: 90 }, // 썸네일+여백만큼 오른쪽 공간 확보
          bottom: 0, // 썸네일 right와 동일하게
          height: 59.3, // 썸네일과 동일
          display: 'flex',
          flexDirection: 'column',
          // justifyContent 제거: 상단 정렬
          alignItems: 'flex-start',
          gap: '2px', // 미세 조정
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'NanumSquareR',
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '17px',
            color: '#777',
            opacity: 0.8,
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isCurrentUser ? '내가 기록한' : `${displayName} 님의`}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'NanumSquareEB',
            fontWeight: 400,
            fontSize: 16,
            lineHeight: '19px',
            color: '#444444',
            opacity: 0.9,
            width: '100%', // 항상 가용 공간 전체 사용
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', // 1줄만 보이고 초과시 ...
          }}
        >
          {highlight.bookTitle}
        </Typography>
      </Box>

      {/* 썸네일 이미지 (상단 2/3만 보이게, 카드 하단에 딱 붙게) */}
      <Box
        sx={{
          position: 'absolute',
          right: 16,
          bottom: 0, // 카드 최하단에 딱 붙임
          width: 58.83,
          height: 59.3, // 89px * 2/3 = 약 59.3px (상단 2/3만 보이게)
          borderRadius: '4px 4px 0 0',
          boxShadow: '4px 8px 8px 0px rgba(0,0,0,0.36)', // 더 강한 그림자
          overflow: 'hidden',
          zIndex: 3,
          bgcolor: '#eee',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={thumbnail}
          alt={`${highlight.bookTitle}${highlight.authors ? ` - ${highlight.authors.join(', ')}` : ''}`}
          loading="lazy"
          sx={{
            width: '100%',
            height: '135%', // 컨테이너보다 더 크게
            objectFit: 'cover',
            objectPosition: 'top',
            display: 'block',
            background: '#eee',
          }}
        />
      </Box>
    </Paper>
  );
};

// 로그인 유도 오버레이 컴포넌트
const LoginOverlay = ({ onLogin }) => {
  // 랜덤 배경 이미지 선택
  const randomBackgroundImage = useMemo(() => {
    const images = ['home-01.png', 'home-02.png', 'home-03.png', 'home-04.png', 'home-05.png', 'home-06.png', 'home-07.png', 'home-08.png'];
    return images[Math.floor(Math.random() * images.length)];
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: '16px',
        p: 0,
      }}
    >
      {/* 배경 이미지 */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(/images/${randomBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(6px) brightness(0.6)',
          zIndex: 0,
        }}
      />
      
      {/* 그라데이션 오버레이 */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.7) 100%)',
          zIndex: 1,
        }}
      />

      {/* 메인 콘텐츠 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          p: 4,
          width: '100%',
          height: '100%',
        }}
      >
        <Typography 
          sx={{ 
            fontWeight: 700, 
            color: 'white', 
            fontSize: { xs: '1.4rem', sm: '1.6rem' },
            textAlign: 'center',
            textShadow: '0 4px 12px rgba(0,0,0,0.5)',
            fontFamily: 'NanumSquareEB',
            lineHeight: 1.3,
          }}
        >
          독서 기억을<br />
          기록으로 남겨보세요
        </Typography>
        <Button
          variant="contained"
          onClick={onLogin}
          startIcon={
            <Box
              component="img"
              src="data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 48 48'%3e%3cpath fill='%23EA4335' d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'/%3e%3cpath fill='%234285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'/%3e%3cpath fill='%23FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'/%3e%3cpath fill='%2334A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'/%3e%3cpath fill='none' d='M0 0h48v48H0z'/%3e%3c/svg%3e"
              alt="Google"
              sx={{ width: 18, height: 18 }}
            />
          }
          sx={{
            bgcolor: 'white',
            color: '#333',
            fontWeight: 600,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            px: { xs: 3, sm: 4 },
            py: { xs: 1.2, sm: 1.5 },
            borderRadius: '50px',
            boxShadow: '0 8px 32px rgba(255,255,255,0.3), 0 4px 16px rgba(0,0,0,0.2)',
            textTransform: 'none',
            fontFamily: 'NanumSquareB',
            letterSpacing: '0.5px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: '#f8f9fa',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(255,255,255,0.4), 0 6px 20px rgba(0,0,0,0.3)',
            },
            '&:active': {
              transform: 'translateY(0px)',
            }
          }}
        >
          구글 계정으로 시작하기
        </Button>
      </Box>
    </Box>
  );
};

function Home() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [authLoading, setAuthLoading] = useState(true);
  const [recentBooks, setRecentBooks] = useState([]);
  const [lastBook, setLastBook] = useState(null);
  const [userHighlightsByBook, setUserHighlightsByBook] = useState({});
  const [loadingLastBook, setLoadingLastBook] = useState(true);
  const [loadingHighlights, setLoadingHighlights] = useState(true);
  const [recommendedBooks, setRecommendedBooks] = useState([]);

  // 하이라이트 관련 상태 추가
  const [allHighlights, setAllHighlights] = useState([]);
  const [displayedHighlights, setDisplayedHighlights] = useState([]);
  const [lastHighlightDoc, setLastHighlightDoc] = useState(null);
  const [hasMoreHighlights, setHasMoreHighlights] = useState(true);
  const [loadingMoreHighlights, setLoadingMoreHighlights] = useState(false);
  const [highlightsError, setHighlightsError] = useState(null);
  const HIGHLIGHTS_PER_PAGE = 15; // 한 번에 로드할 하이라이트 수

  // 무한 스크롤을 위한 Intersection Observer 설정
  const observerTarget = useRef(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [isbn, setIsbn] = useState('');
  const [popularBooks, setPopularBooks] = useState([]);
  const [loadingPopular, setLoadingPopular] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // --- Add state for Friend's Highlights --- 
  const [followingIds, setFollowingIds] = useState([]); // IDs of users the current user follows
  const [friendHighlights, setFriendHighlights] = useState([]); // Highlights from followed users
  const [loadingFriendHighlights, setLoadingFriendHighlights] = useState(true);

  // State for controlling displayed highlight counts
  const [displayedUserHighlightsCount, setDisplayedUserHighlightsCount] = useState(4);
  const [displayedFriendHighlightsCount, setDisplayedFriendHighlightsCount] = useState(4);
  
  // State for Info Modal/Popover
  const [infoOpen, setInfoOpen] = useState(false);

  // 추가된 상태
  const [inAppBrowserModalOpen, setInAppBrowserModalOpen] = useState(false);

  // 비로그인 상태에서 스크롤 80% 이상 내리면 로그인 오버레이 표시
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);

  // 비로그인 상태에서 스크롤 이벤트 처리
  useEffect(() => {
    if (currentUser) {
      setShowLoginOverlay(false);
      return;
    }

    const handleScrollForLogin = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercentage = scrollY / docHeight;
      
      if (scrollPercentage >= 0.6) { // 60% 스크롤 시 로그인 오버레이 표시
        setShowLoginOverlay(true);
      } else {
        setShowLoginOverlay(false);
      }
    };

    window.addEventListener('scroll', handleScrollForLogin, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollForLogin);
  }, [currentUser]);

  // 새로운 레이아웃을 위한 상태 변수들
  const [readingBooks, setReadingBooks] = useState([]);
  const [notStartedBooks, setNotStartedBooks] = useState([]);
  const [loadingNotStarted, setLoadingNotStarted] = useState(true);

  // Handler for Info icon click
  const handleInfoClick = () => {
    navigate('/privacy');
  };

  const handleInfoClose = () => {
    setInfoOpen(false);
  };

  // --- Logic to determine cards for "Recent Reading" section ---
  const recentReadingCards = useMemo(() => {
    if (!currentUser || Object.keys(userHighlightsByBook).length === 0) {
      return []; // Return empty if no user or no highlights data
    }

    const bookEntries = Object.entries(userHighlightsByBook).map(([key, value]) => ({ key, ...value }));
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    let recentHighlightBook = null;
    let revisitBook = null;
    let mostHighlightedBook = null;

    // Find Recent Highlight Book
    recentHighlightBook = bookEntries.reduce((latest, book) => {
      if (!latest || (book.lastHighlightDate && book.lastHighlightDate > latest.lastHighlightDate)) {
        return book;
      }
      return latest;
    }, null);

    // Find Revisit Book (Oldest highlight among those older than 30 days)
    revisitBook = bookEntries
      .filter(book => book.lastHighlightDate && book.lastHighlightDate.toMillis() < thirtyDaysAgo)
      .sort((a, b) => (a.lastHighlightDate?.toMillis() || 0) - (b.lastHighlightDate?.toMillis() || 0)) // Sort oldest first
      [0]; // Get the oldest one if any

    // Find Most Highlighted Book
    mostHighlightedBook = bookEntries.reduce((most, book) => {
      if (!most || book.highlights.length > most.highlights.length) {
        return book;
      }
      return most;
    }, null);

    // De-duplicate based on priority: Recent > Revisit > Most Highlighted
    const finalCards = [];
    const addedBookKeys = new Set();

    if (recentHighlightBook) {
      finalCards.push({ ...recentHighlightBook, type: 'Recent Highlight' });
      addedBookKeys.add(recentHighlightBook.key);
    }

    if (revisitBook && !addedBookKeys.has(revisitBook.key)) {
      finalCards.push({ ...revisitBook, type: 'Revisit' });
      addedBookKeys.add(revisitBook.key);
    }

    if (mostHighlightedBook && !addedBookKeys.has(mostHighlightedBook.key)) {
      finalCards.push({ ...mostHighlightedBook, type: 'Most Highlighted' });
      addedBookKeys.add(mostHighlightedBook.key);
    }

    return finalCards;

  }, [userHighlightsByBook, currentUser]); // Recalculate when highlights or user change
  // --- End of Recent Reading logic ---

  // Intersection Observer 설정
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, []);

  // 하이라이트 로드 함수 (Search.jsx와 동일한 방식)
  const fetchHighlights = useCallback(async (pageNum = 1, append = false) => {
    try {
      const highlightsRef = collection(db, 'highlights');
      const q = query(
        highlightsRef,
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const allHighlights = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // 랜덤 10개 선택
      const shuffled = allHighlights.sort(() => 0.5 - Math.random());
      const selectedHighlights = shuffled.slice(0, 10);
      if (append) {
        setDisplayedHighlights(prev => [...prev, ...selectedHighlights]);
      } else {
        setDisplayedHighlights(selectedHighlights);
      }
      setHasMoreHighlights(selectedHighlights.length === 10);
    } catch (err) {
      if (!append) setDisplayedHighlights([]);
    }
  }, []);

  // 최초 1회만 하이라이트 로딩
  useEffect(() => {
    fetchHighlights(1, false);
  }, [fetchHighlights]);

  // 스크롤 이벤트로 추가 하이라이트 로딩 (Search.jsx와 동일)
  useEffect(() => {
    const handleScroll = () => {
      if (!hasMoreHighlights || loadingMoreHighlights) return;
      const scrollY = window.scrollY || window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercentage = scrollY / docHeight;
      if (scrollPercentage >= 0.8) {
        setLoadingMoreHighlights(true);
        fetchHighlights(1, true).finally(() => setLoadingMoreHighlights(false));
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMoreHighlights, loadingMoreHighlights, fetchHighlights]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      console.log("Auth state changed, user:", user);
      setCurrentUser(user);
      // Reset data loading states only if user is logged in
      if (user) {
        setLoadingLastBook(true);
        setLoadingHighlights(true);
        setLoadingFriendHighlights(true);
        setFollowingIds([]); // Reset following list on login
        setFriendHighlights([]); // Reset friend highlights
      } else {
        // Ensure data loading states are false if logged out
        setLastBook(null);
        setUserHighlightsByBook({});
        setFriendHighlights([]);
        setFollowingIds([]);
        setLoadingLastBook(false);
        setLoadingHighlights(false);
        setLoadingFriendHighlights(false);
      }
      setDisplayedUserHighlightsCount(4); // Reset counts regardless of login state
      setDisplayedFriendHighlightsCount(4);
      setAuthLoading(false); // Auth check is complete
    });
    return unsubscribe;
  }, []);

  // Reading books 로딩
  useEffect(() => {
    console.log(`[Home] Reading books useEffect 실행 - currentUser:`, currentUser);
    if (!currentUser) {
      console.log(`[Home] 사용자가 로그인하지 않음 - Reading books 로딩 중단`);
      setReadingBooks([]);
      setLoadingLastBook(false);
      return;
    }
    console.log(`[Home] 사용자 로그인됨 - Reading books 로딩 시작 (uid: ${currentUser.uid})`);
    setLoadingLastBook(true);
    const booksRef = collection(db, 'books');
    
    // 더 안전한 쿼리 방식 사용
    const q = query(
      booksRef,
      where('uid', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(20) // 더 많은 책을 가져와서 클라이언트에서 필터링
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const allBooks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // 디버깅 로그 추가
        console.log(`[Home] 전체 책 ${allBooks.length}개 로드됨`);
        console.log(`[Home] 현재 사용자: ${currentUser?.uid}`);
        allBooks.forEach(book => {
          console.log(`[Home] 책: ${book.title}, readStatus: ${book.readStatus}, uid: ${book.uid}`);
        });
        
        // readStatus 분포 확인
        const statusDistribution = allBooks.reduce((acc, book) => {
          acc[book.readStatus || 'undefined'] = (acc[book.readStatus || 'undefined'] || 0) + 1;
          return acc;
        }, {});
        console.log(`[Home] readStatus 분포:`, statusDistribution);
        
        // 클라이언트에서 읽고 있는 책만 필터링 (BookMemoEdit 상태 반영)
        const readingBooks = allBooks.filter(book => {
          const readStatus = book.readStatus;
          // BookMemoEdit에서 설정하는 정확한 값들만 체크
          const isReading = readStatus === 'reading';
          
          if (isReading) {
            console.log(`[Home] 읽는 중인 책 발견: ${book.title} (readStatus: ${book.readStatus})`);
          } else {
            console.log(`[Home] 읽는 중이 아닌 책: ${book.title} (readStatus: ${book.readStatus})`);
          }
          return isReading;
        }).slice(0, 5); // 최대 5개만 표시
        
        console.log(`[Home] 읽는 중인 책 ${readingBooks.length}개 필터링됨`);
        console.log(`[Home] 필터링된 읽는 중인 책들:`, readingBooks.map(book => ({ title: book.title, readStatus: book.readStatus })));
        setReadingBooks(readingBooks);
        setLoadingLastBook(false);
      },
      (error) => {
        console.error("읽는 중인 책 로딩 오류:", error);
        setReadingBooks([]);
        setLoadingLastBook(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Not started books 로딩
  useEffect(() => {
    console.log(`[Home] Not started books useEffect 실행 - currentUser:`, currentUser);
    if (!currentUser) {
      console.log(`[Home] 사용자가 로그인하지 않음 - Not started books 로딩 중단`);
      setNotStartedBooks([]);
      setLoadingNotStarted(false);
      return;
    }
    console.log(`[Home] 사용자 로그인됨 - Not started books 로딩 시작 (uid: ${currentUser.uid})`);
    setLoadingNotStarted(true);
    const booksRef = collection(db, 'books');
    
    // 더 안전한 쿼리 방식 사용
    const q = query(
      booksRef,
      where('uid', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(20) // 더 많은 책을 가져와서 클라이언트에서 필터링
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const allBooks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // 디버깅 로그 추가
        console.log(`[Home] 전체 책 ${allBooks.length}개 로드됨 (To Read)`);
        allBooks.forEach(book => {
          console.log(`[Home] 책: ${book.title}, readStatus: ${book.readStatus}, uid: ${book.uid}`);
        });
        
        // 클라이언트에서 읽기 전 책만 필터링 (BookMemoEdit 상태 반영)
        const notStartedBooks = allBooks.filter(book => {
          const readStatus = book.readStatus;
          // BookMemoEdit에서 설정하는 정확한 값들만 체크 (기본값 포함)
          const isNotStarted = readStatus === 'not_started' || 
                              readStatus === undefined || 
                              readStatus === null;
          
          if (isNotStarted) {
            console.log(`[Home] 읽기 전 책 발견: ${book.title} (readStatus: ${book.readStatus})`);
          } else {
            console.log(`[Home] 읽기 전이 아닌 책: ${book.title} (readStatus: ${book.readStatus})`);
          }
          return isNotStarted;
        }).slice(0, 5); // 최대 5개만 표시
        
        console.log(`[Home] 읽기 전 책 ${notStartedBooks.length}개 필터링됨`);
        setNotStartedBooks(notStartedBooks);
        setLoadingNotStarted(false);
      },
      (error) => {
        console.error("읽기 전 책 로딩 오류:", error);
        setNotStartedBooks([]);
        setLoadingNotStarted(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setUserHighlightsByBook({});
      setLoadingHighlights(false);
      return;
    }
    setLoadingHighlights(true);

    const highlightsRef = collection(db, 'highlights');
    const q = query(
      highlightsRef,
      where('uid', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const highlights = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const grouped = highlights.reduce((acc, highlight) => {
          const bookId = highlight.bookDocId || highlight.bookId;
          if (!bookId) return acc;
          if (!acc[bookId]) {
            acc[bookId] = {
              bookTitle: highlight.bookTitle || '제목 없음',
              bookThumbnail: highlight.bookThumbnail || '/images/no-cover.png',
              highlights: [],
              lastHighlightDate: null,
              lastHighlightText: null,
              bookDocId: highlight.bookDocId || `${currentUser.uid}_${bookId}`
            };
          }
          acc[bookId].highlights.push(highlight);
          if (!acc[bookId].lastHighlightDate || (highlight.createdAt && highlight.createdAt > acc[bookId].lastHighlightDate)) {
            acc[bookId].lastHighlightDate = highlight.createdAt;
            acc[bookId].lastHighlightText = highlight.text;
          }
          return acc;
        }, {});
        const sortedGrouped = Object.entries(grouped)
          .sort(([, a], [, b]) => (b.lastHighlightDate?.toMillis() || 0) - (a.lastHighlightDate?.toMillis() || 0))
          .reduce((acc, [key, value]) => { acc[key] = value; return acc; }, {});
        setUserHighlightsByBook(sortedGrouped);
        setLoadingHighlights(false);
      },
      (error) => {
        setUserHighlightsByBook({});
        setLoadingHighlights(false);
      }
    );
    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setFollowingIds([]);
      return;
    }
    const followingRef = collection(db, 'users', currentUser.uid, 'following');
    const q = query(followingRef);
    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        setFollowingIds(snapshot.docs.map(doc => doc.id));
      },
      (error) => {
        setFollowingIds([]);
      }
    );
    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!followingIds || followingIds.length === 0) {
      setFriendHighlights([]);
      setLoadingFriendHighlights(false);
      console.log("No users followed, skipping friend highlights fetch.");
      return;
    }

    setLoadingFriendHighlights(true);
    console.log(`Fetching highlights for ${followingIds.length} followed users.`);

    // Firestore 'in' query limitation (max 30 as of recent updates, but check docs)
    // Let's fetch highlights in chunks if needed, but for now, limit query to 30 uids
    const MAX_IN_QUERY_SIZE = 30; 
    const queryIds = followingIds.length > MAX_IN_QUERY_SIZE ? followingIds.slice(0, MAX_IN_QUERY_SIZE) : followingIds;
    if (followingIds.length > MAX_IN_QUERY_SIZE) {
      console.warn(`Following more than ${MAX_IN_QUERY_SIZE} users, only fetching highlights from the first ${MAX_IN_QUERY_SIZE}. Consider pagination or different strategy.`);
    }

    const highlightsRef = collection(db, 'highlights');
    const q = query(
      highlightsRef,
      where('uid', 'in', queryIds), 
      orderBy('createdAt', 'desc'),
      limit(50) 
    );

    const unsubscribe = onSnapshot(q,
      async (snapshot) => {
        const highlightsData = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const highlight = { id: doc.id, ...doc.data() };
            
            // ✅ Construct the correct book document ID for navigation
            const friendUid = highlight.uid; // UID of the highlight creator (book owner)
            const cleanIsbn = (highlight.bookId || '').split(' ')[0]; // Assuming bookId field stores ISBN
            let bookDocIdForNav = null;
            if (friendUid && cleanIsbn) {
              bookDocIdForNav = `${friendUid}_${cleanIsbn}`;
            } else {
              console.warn(`[Home - FriendHighlight] Missing friendUid or cleanIsbn for highlight ID: ${highlight.id}`, highlight);
            }

            // Basic info from highlight doc itself
            const basicData = {
              ...highlight,
              bookId: cleanIsbn, // Keep cleaned ISBN if needed elsewhere
              bookTitle: highlight.bookTitle || "책 정보 없음",
              bookThumbnail: highlight.bookThumbnail || "/images/no-cover.png",
              friendUserId: friendUid, // Keep track of who made the highlight
              friendName: highlight.userName || "익명", // Get creator name from highlight doc
              bookDocIdForNav: bookDocIdForNav // ✅ Store the correct ID for navigation
            };
            return basicData;
          })
        );

        setFriendHighlights(highlightsData);
        setLoadingFriendHighlights(false);
        console.log(`${highlightsData.length} friend highlights loaded.`);
      },
      (error) => {
        console.error("Error fetching friend highlights:", error);
        setFriendHighlights([]);
        setLoadingFriendHighlights(false);
      }
    );

    return () => unsubscribe();

  }, [followingIds]); // Re-run when the list of followed IDs changes

  useEffect(() => {
    let mounted = true;
    let unsubscribeBooks = null;

    const fetchRecentBooks = async () => {
      if (!currentUser) {
        setRecentBooks([]);
        return;
      }
      try {
        const booksRef = collection(db, 'books');
        const q = query(
          booksRef,
          where('uid', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(10)
        );

        unsubscribeBooks = onSnapshot(q, 
          (snapshot) => {
            if (!mounted) return;
            const books = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRecentBooks(books);
          },
          (error) => {
            if (!mounted) return;
            console.error("최근 등록 도서 목록 구독 오류:", error);
          }
        );
      } catch (error) {
        if (!mounted) return;
        console.error("최근 등록 도서 목록 초기화 오류:", error);
      }
    };

    fetchRecentBooks();

    return () => {
      mounted = false;
      if (unsubscribeBooks) {
        try {
          unsubscribeBooks();
        } catch (error) {
          console.error("구독 해제 오류:", error);
        }
      }
    };
  }, [currentUser]);

  useEffect(() => {
    setLoadingPopular(true);
    const fetchPopularBooks = async () => {
      try {
        const booksRef = collection(db, "books");
        // ✅ Query updated based on ownershipCount
        const q = query(
          booksRef, 
          where("ownershipCount", ">", 0), // Filter for count > 0
          orderBy("ownershipCount", "desc"), // Order by count descending
          limit(5) // Limit to top 5
        ); 
        const snapshot = await getDocs(q);
        
        const booksData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPopularBooks(booksData);
      } catch (error) { 
        console.error("인기 책 로딩 오류:", error);
        // Set empty array on error to avoid breaking the UI
        setPopularBooks([]); 
      } finally {
        setLoadingPopular(false);
      }
    };

    fetchPopularBooks();
    // No dependencies needed as this should run once on mount or be triggered differently if needed
  }, []); 

  useEffect(() => {
    if (authLoading) {
      setLoading(true); // Still waiting for auth check
    } else if (!currentUser) {
       // If auth check is done and user is not logged in, stop loading.
       setLoading(false);
    } else {
      // If auth check is done and user IS logged in, wait for all relevant data.
      if (!loadingLastBook && !loadingNotStarted && !loadingHighlights && !loadingPopular && !loadingFriendHighlights) {
        setLoading(false);
      } else {
        setLoading(true);
      }
    }
  }, [authLoading, currentUser, loadingLastBook, loadingNotStarted, loadingHighlights, loadingPopular, loadingFriendHighlights]);

  const handleAddBook = async () => {
    if (!isbn.trim()) {
      alert("ISBN을 입력해주세요.");
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        alert("로그인이 필요합니다.");
        return;
      }

      // Consider adding loading indicator during fetch
      const response = await fetch(`https://dapi.kakao.com/v3/search/book?target=isbn&query=${isbn}`, {
        headers: {
          'Authorization': `KakaoAK ${process.env.REACT_APP_KAKAO_API_KEY}`
        }
      });
      
      const data = await response.json();
      
      if (data.documents && data.documents.length > 0) {
        const bookInfo = data.documents[0];
        const bookIdForDoc = `${user.uid}_${isbn}`; // Create a predictable ID if needed, or let Firestore generate
        
        // Check if book already exists (optional, based on bookIdForDoc or ISBN)
        // const bookRef = doc(db, "books", bookIdForDoc);
        // const docSnap = await getDoc(bookRef);
        // if (docSnap.exists()) { alert("이미 등록된 책입니다."); return; }

        await addDoc(collection(db, "books"), { // Using addDoc generates unique ID
          title: bookInfo.title,
          authors: bookInfo.authors || [],
          publisher: bookInfo.publisher || '',
          thumbnail: bookInfo.thumbnail || '/images/no-cover.png',
          isbn: isbn, // Store ISBN for potential lookups
          uid: user.uid,
          createdAt: serverTimestamp()
        });

        alert("책이 성공적으로 등록되었습니다!");
        setOpenAddDialog(false);
        setIsbn('');
      } else {
        alert("해당 ISBN의 책을 찾을 수 없습니다.");
      }
    } catch (error) {
      console.error("책 등록 오류:", error);
      alert("책 등록에 실패했습니다.");
    } finally {
      // Stop loading indicator
    }
  };

  // Handler to show more user highlights incrementally
  const handleShowMoreUserHighlights = () => {
    setDisplayedUserHighlightsCount(prevCount => 
      Math.min(prevCount + 4, Object.keys(userHighlightsByBook).length)
    );
  };

  // Handler to show more friend highlights incrementally
  const handleShowMoreFriendHighlights = () => {
    setDisplayedFriendHighlightsCount(prevCount => 
      Math.min(prevCount + 4, friendHighlights.length)
    );
  };

  // ✅ 수정된 로그인 핸들러 함수
  const handleSignIn = async () => {
    try {
      const result = await handleGoogleLogin(
        // 성공 콜백
        async (user) => {
          // 로그인 성공 시 Firestore 사용자 문서 확인 및 생성
          if (user) {
            const userDocRef = doc(db, "users", user.uid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (!userDocSnap.exists()) {
              // 첫 회원가입인 경우 Firestore에 사용자 문서 생성
              console.log(`Firestore에 새 사용자 등록: ${user.uid}`);
              try {
                await setDoc(userDocRef, {
                  displayName: user.displayName,
                  email: user.email,
                  photoURL: user.photoURL,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp()
                });
                setSnackbar({ open: true, message: '회원가입이 완료되었습니다!', severity: 'success' });
              } catch (firestoreError) {
                console.error("Firestore 사용자 문서 생성 오류:", firestoreError);
                setSnackbar({ open: true, message: '사용자 정보 저장 중 오류 발생', severity: 'error' });
              }
            } else {
              console.log(`기존 사용자 로그인: ${user.uid}`);
              setSnackbar({ open: true, message: '로그인 되었습니다.', severity: 'success' });
            }
          }
        },
        // 오류 콜백
        (error) => {
          console.error("로그인 오류:", error);
          if (error.code === 'auth/popup-closed-by-user') {
            setSnackbar({ open: true, message: '로그인 팝업이 닫혔습니다.', severity: 'warning' });
          } else {
            setSnackbar({ open: true, message: '로그인 중 오류가 발생했습니다.', severity: 'error' });
          }
        },
        // 인앱 브라우저 감지 콜백
        () => {
          setInAppBrowserModalOpen(true);
        }
      );
    } catch (error) {
      console.error("로그인 프로세스 오류:", error);
      setSnackbar({ open: true, message: '로그인 중 오류가 발생했습니다.', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center min-h-[calc(100vh-100px)]">
        <CircularProgress size={28} color="inherit" className="text-gray-400"/>
      </Box>
    );
  }

  return (
    <>
      <SEO 
        title="스냅로그: 책 속 한 문장 기록, 스냅로그"
        description="책에서 마음에 남는 문장을 기록하세요. 스냅로그는 나의 독서기록을 남기고, 공유하고, 되돌아볼 수 있는 독서기록 서비스입니다."
        url="/"
      />
      {/* Add Box wrapper to contain AppBar and main content */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* --- NEW Header AppBar --- */}
      <AppBar 
        position="static" // Or "fixed" or "static" depending on desired scroll behavior
        elevation={0} // Subtle shadow
        sx={{ bgcolor: 'white' }} // White background

      >
        <Toolbar sx={{ px: 2}}> {/* content hug, 상하 패딩 2px */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            {/* 텍스트 로고 */}
            <Box component="span" sx={{
              fontFamily: 'NanumSquareB',
              fontSize: { xs: '1.4rem' },
              fontWeight: 500,
              letterSpacing: '-0.5px',
              color: '#333333',
              userSelect: 'none',
              lineHeight: 1.1,
              display: 'flex',
              alignItems: 'center',
            }}>
              Snap
              <Box component="span" sx={{
                fontWeight: 600,
                color: '#FFD700',
                ml: '2px',
                fontFamily: 'NanumSquareEB',
                letterSpacing: '-0.5px',
                fontSize: { xs: '1.5rem' },
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.05)',
              }}>
                Log
              </Box>
            </Box>
          </Box>

          {/* Info Icon Button */}
          <IconButton 
            onClick={handleInfoClick} 
            size="small" 
            sx={{
              bgcolor: 'white', // White circle background
              border: '1px solid', // Optional: add border for definition
              borderColor: 'grey.300', // Light border color
              width: 32, 
              height: 32,
              position: 'relative',
              zIndex: 15, // 상위 레이어로 설정
              '&:hover': {
                bgcolor: 'grey.100' // Slight hover effect
              }
            }}
          >
            <InfoOutlinedIcon sx={{ color: 'black', fontSize: 20 }} /> {/* Black icon */}
          </IconButton>
        </Toolbar>
      </AppBar>
      {/* --- NEW Main Content Layout --- */}
      <Box component="main" sx={{ flexGrow: 1, overflowY: 'auto', bgcolor: '#f8f9fa' }}>
        <Box sx={{ 
          maxWidth: '600px', 
          mx: 'auto', 
          bgcolor: 'white', 
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          // 포인터 이벤트를 기본적으로 비활성화하여 자식 요소들이 우선순위를 가지도록 설정
          pointerEvents: 'none'
        }}>
          {/* Action Buttons Section - 최상위 레이어 */}
          <Box sx={{ 
            position: 'relative',
            zIndex: 100,
            flexShrink: 0,
            pointerEvents: 'auto',
            mb: currentUser ? 4 : 6
          }}>
            <ActionButtons currentUser={currentUser} navigate={navigate} />
          </Box>

          {/* Compact Book Lists Section - 중간 레이어 (로그인 시에만 표시) */}
          {currentUser && (
            <Box sx={{ 
              position: 'relative',
              zIndex: 50,
              flexShrink: 0,
              pointerEvents: 'auto',
              mb: 4
            }}>
              <CompactBookList 
                title="Reading" 
                books={readingBooks} 
                currentUser={currentUser} 
                navigate={navigate} 
                loading={loadingLastBook} 
              />
              
              <CompactBookList 
                title="To Read" 
                books={notStartedBooks} 
                currentUser={currentUser} 
                navigate={navigate} 
                loading={loadingNotStarted} 
              />
            </Box>
          )}

          {/* Enhanced Book Cards Section - 하위 레이어 */}
          <Box sx={{ 
            mb: 4,
            mt: currentUser ? 0 : 2,
            position: 'relative',
            zIndex: 1,
            flexGrow: 1,
            pointerEvents: 'none'
          }}>
            <Typography sx={{ 
              px: 2, 
              mb: 1,
              fontWeight: '700', 
              fontFamily: 'NanumSquareEB', 
              fontSize: '18px', 
              color: '#333',
              cursor: 'default',
              userSelect: 'none',
              position: 'relative',
              zIndex: 10,
              pointerEvents: 'none' // 텍스트는 클릭 이벤트 방지
            }}>
              기록 모아보기
            </Typography>
            <EnhancedBookCards 
              currentUser={currentUser} 
              navigate={navigate} 
              followingIds={followingIds} 
              authLoading={authLoading} 
            />
          </Box>

          {/* 비로그인 상태에서 스크롤 80% 이상 내리면 로그인 오버레이 부드럽게 표시 */}
          {!currentUser && (
            <Box sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1300,
              width: '100vw',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              m: 0,
              p: 0,
              pointerEvents: 'none',
            }}>
              <Box sx={{
                width: '100vw',
                maxWidth: '100vw',
                borderRadius: 0,
                boxShadow: 6,
                bgcolor: 'background.paper',
                opacity: showLoginOverlay ? 0.98 : 0,
                transition: 'opacity 0.4s ease-in-out',
                pointerEvents: showLoginOverlay ? 'auto' : 'none',
                transform: showLoginOverlay ? 'translateY(0)' : 'translateY(20px)',
              }}>
                <LoginOverlay onLogin={handleSignIn} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Dialogs and Modals */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} fullWidth maxWidth="sm">
         <DialogTitle>새 책 추가 (ISBN)</DialogTitle>
         <DialogContent>
           <TextField
             autoFocus
             margin="dense"
             label="ISBN"
             type="text"
             fullWidth
             variant="outlined"
             value={isbn}
             onChange={(e) => setIsbn(e.target.value)}
             helperText="책 뒷면의 ISBN 10 또는 13자리 숫자를 입력하세요."
           />
         </DialogContent>
         <DialogActions sx={{ px: 3, pb: 2 }}>
           <Button onClick={() => setOpenAddDialog(false)}>취소</Button>
           <Button 
             onClick={handleAddBook} 
             variant="contained" 
             sx={{ 
               bgcolor: 'grey.900',
               color: 'white',
               '&:hover': { bgcolor: 'grey.800' }
             }}
           >
             추가
           </Button>
         </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 인앱 브라우저 감지 모달 */}
      <InAppBrowserModal 
        open={inAppBrowserModalOpen} 
        onClose={() => setInAppBrowserModalOpen(false)} 
      />
    </Box>
    </>
  );
}

export default Home;

